import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Wrench, CalendarDays, Users, Settings, TrendingUp, 
  Mail, RefreshCw, ChevronRight, X, Check, Clock, AlertTriangle,
  Gauge, Phone, Send, Bell, BarChart3, Bike, Search, Upload, FileText, PhoneCall, Calendar,
  Play, XCircle, RotateCcw, Eye, Plus, Trash2, Lock, Palmtree, Thermometer, GraduationCap,
  ChevronLeft, FileBarChart, Download, Key, History, Menu
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './index.css';
import Planning from './modules/sav-atelier/Planning';
import TestRides from './modules/crm-vente/TestRides';
import SaisieVT from './modules/sav-atelier/SaisieVT';
import LicenseAdmin from './components/LicenseAdmin';
import AdaptiveDashboard from './components/AdaptiveDashboard';
import NotificationCenter from './components/NotificationCenter';
import AuditLogView from './components/AuditLogView';
import { LicenseProvider, useLicense } from './contexts/LicenseContext';
import { 
  dashboardAPI, techniciensAPI, clientsAPI, motosAPI, 
  ordresTravailAPI, planningAPI, leadsAPI, bexioAPI, seedAPI, authAPI,
  tentativesAppelAPI, automatismesAPI, absencesAPI, adminAPI, prestationsAPI,
  rapportsAPI, exportAPI, licenseAPI, testRidesAPI
} from './api';
import { 
  vtToHM, fmtDate, fmtCHF, OT_STATUTS, PIPELINE_STAGES, 
  getWeekDates, getWeekNumber, JOURS_COURTS, JOURS_SEMAINE, MOIS_FR, chargeColor, chargeTextColor, VT_TO_MIN 
} from './utils';

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('nnm_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('nnm_token');
    if (token) {
      authAPI.getMe()
        .then(res => {
          setUser(res.data);
          localStorage.setItem('nnm_user', JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem('nnm_token');
          localStorage.removeItem('nnm_user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login(email, password);
    localStorage.setItem('nnm_token', res.data.access_token);
    localStorage.setItem('nnm_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('nnm_token');
    localStorage.removeItem('nnm_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError('Email ou mot de passe incorrect');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeedAndLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      await seedAPI.seed();
      // Create a test user if needed
      try {
        await authAPI.register({
          email: 'admin@nnm.ch',
          password: 'admin123',
          prenom: 'Admin',
          nom: 'NNM'
        });
      } catch {
        // User might already exist
      }
      await login('admin@nnm.ch', 'admin123');
      navigate('/');
    } catch (err) {
      setError('Erreur lors de l\'initialisation');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">No Name Motorcycles</h1>
        <p className="auth-subtitle">Connexion au CRM</p>
        
        {error && (
          <div className="badge badge-red mb-4 w-full justify-center py-2">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-[var(--text3)] uppercase tracking-wider mb-2">Email</label>
            <input
              type="email"
              className="input w-full"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@nnm.ch"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="block text-xs text-[var(--text3)] uppercase tracking-wider mb-2">Mot de passe</label>
            <input
              type="password"
              className="input w-full"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="login-password"
            />
          </div>
          <button 
            type="submit" 
            className="btn btn-primary w-full justify-center"
            disabled={isLoading}
            data-testid="login-submit"
          >
            {isLoading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-[var(--border)]">
          <button
            onClick={handleSeedAndLogin}
            className="btn btn-ghost w-full justify-center"
            disabled={isLoading}
            data-testid="seed-and-login"
          >
            <RefreshCw size={14} />
            Initialiser les données de démo
          </button>
          <p className="text-xs text-[var(--text3)] text-center mt-2">
            Crée les données de test et connecte avec admin@nnm.ch
          </p>
        </div>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--bg)]">
        <RefreshCw className="animate-spin text-[var(--accent)]" size={32} />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Navigation items avec module associé
const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, module: null }, // Toujours visible
  { key: 'dashboard-atelier', label: 'Dashboard atelier', icon: Gauge, module: 'sav-atelier' },
  { section: 'CRM VENTE', module: 'crm-vente' },
  { key: 'pipeline', label: 'Pipeline', icon: TrendingUp, module: 'crm-vente' },
  { key: 'test-rides', label: 'Test Rides', icon: Bike, module: 'crm-vente' },
  { key: 'automatismes', label: 'Automatismes', icon: Mail, module: 'crm-vente' },
  { section: 'SAV ATELIER', module: 'sav-atelier' },
  { key: 'ot', label: 'Ordres de travail', icon: Wrench, module: 'sav-atelier' },
  { key: 'planning', label: 'Planning VT', icon: CalendarDays, module: 'sav-atelier' },
  { key: 'saisie-vt', label: 'Saisie VT Réalisés', icon: Clock, module: 'sav-atelier' },
  { key: 'parc-motos', label: 'Parc Motos / VIN', icon: Bike, module: 'sav-atelier' },
  { section: 'PERSONNEL', module: 'gestion-personnel' },
  { key: 'equipe', label: 'Équipe & Absences', icon: Users, module: 'gestion-personnel' },
  { section: 'INTÉGRATIONS', module: 'bexio-sync' },
  { key: 'bexio', label: 'Bexio Sync', icon: RefreshCw, module: 'bexio-sync' },
  { section: 'ADMINISTRATION', module: null }, // Toujours visible
  { key: 'licenses', label: 'Licences', icon: Key, module: null },
  { key: 'audit-log', label: 'Historique', icon: History, module: null },
];

// Sidebar Component avec filtrage par licence
const Sidebar = ({ currentView, setView, bexioStatus, isMobileOpen, setMobileOpen }) => {
  const { logout } = useAuth();
  const { license, hasModule } = useLicense();

  // Filtrer les éléments de navigation selon la licence
  const filteredNavItems = NAV_ITEMS.filter(item => {
    // Les sections sont filtrées si le module correspondant n'est pas actif
    if (item.section) {
      if (item.module === null) return true; // Sections toujours visibles
      return hasModule(item.module);
    }
    // Les items sans module sont toujours visibles (dashboard, licences)
    if (item.module === null) return true;
    // Les items avec module sont visibles si le module est actif
    return hasModule(item.module);
  });

  const handleNavClick = (key) => {
    setView(key);
    setMobileOpen(false); // Ferme le menu mobile après sélection
  };

  return (
    <>
      {/* Overlay mobile */}
      {isMobileOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside className={`sidebar ${isMobileOpen ? 'mobile-open' : ''}`} data-testid="sidebar">
        <div className="sidebar-close-mobile" onClick={() => setMobileOpen(false)}>
          <X size={24} />
        </div>
        <div className="sidebar-logo">
        <div className="sidebar-title">No Name</div>
        <div className="sidebar-title">Motorcycles</div>
        <div className="sidebar-subtitle">CRM Atelier</div>
        <div className="bexio-status">
          <span className={`bexio-dot ${bexioStatus?.connected ? '' : 'bg-red-500'}`} />
          <span>Bexio {bexioStatus?.connected ? 'connecté' : 'déconnecté'}</span>
        </div>
        {license && license.has_license && license.days_remaining <= 30 && (
          <div className="mt-2 text-xs text-center" style={{ color: license.days_remaining <= 7 ? 'var(--red)' : 'var(--orange)' }}>
            Licence: {license.days_remaining}j restants
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        {filteredNavItems.map((item, idx) => {
          if (item.section) {
            return <div key={idx} className="nav-section">{item.section}</div>;
          }
          const Icon = item.icon;
          return (
            <div
              key={item.key}
              className={`nav-item ${currentView === item.key ? 'active' : ''}`}
              onClick={() => handleNavClick(item.key)}
              data-testid={`nav-${item.key}`}
            >
              <span className="nav-icon"><Icon size={16} /></span>
              <span>{item.label}</span>
              {item.badge && <span className="nav-badge">{item.badge}</span>}
            </div>
          );
        })}
      </nav>

      <div className="p-4 border-t border-[var(--border)]">
        <button onClick={logout} className="btn btn-ghost w-full justify-center text-xs">
          Déconnexion
        </button>
      </div>
    </aside>
    </>
  );
};

// Dashboard Component
const Dashboard = ({ setView, stats, ots, techniciens, clients, motos, leads }) => {
  const otsProvisoires = ots.filter(o => o.statut === 'provisoire');
  const leadsActifs = leads.filter(l => !['gagne', 'perdu'].includes(l.statut));

  const getClient = (id) => clients.find(c => c.id === id);
  const getMoto = (id) => motos.find(m => m.id === id);
  const getTech = (id) => techniciens.find(t => t.id === id);

  return (
    <div data-testid="dashboard-view">
      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">VT semaine planifiés</div>
          <div className="stat-value">{stats?.vt_planifies_actifs || 0}</div>
          <div className="stat-sub">{vtToHM(stats?.vt_planifies_actifs || 0)} de main d'œuvre</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">OT à confirmer</div>
          <div className="stat-value" style={{ color: stats?.ot_provisoires > 0 ? 'var(--yellow)' : 'inherit' }}>
            {stats?.ot_provisoires || 0}
          </div>
          <div className="stat-sub">Offres Bexio en attente</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pipeline vente</div>
          <div className="stat-value">{fmtCHF(stats?.pipeline_total || 0)}</div>
          <div className="stat-sub">{stats?.leads_actifs || 0} leads actifs</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Taux occupation</div>
          <div className="stat-value">{stats?.taux_occupation || 0}%</div>
          <div className="stat-sub">Semaine courante</div>
        </div>
      </div>

      <div className="grid-2">
        {/* OT Provisoires */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">OT provisoires — à confirmer</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setView('ot')}>
              Tous les OT <ChevronRight size={14} />
            </button>
          </div>
          <div className="card-body">
            {otsProvisoires.length === 0 ? (
              <p className="text-[var(--text3)] text-sm">Aucun OT provisoire ✓</p>
            ) : (
              otsProvisoires.slice(0, 5).map(ot => {
                const client = getClient(ot.client_id);
                const moto = getMoto(ot.moto_id);
                return (
                  <div key={ot.id} className="ot-row provisoire" data-testid={`ot-row-${ot.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-condensed font-bold">{ot.reference}</span>
                        {ot.bexio_offre_ref && (
                          <span className="text-xs text-[var(--text3)] ml-2">⬡ {ot.bexio_offre_ref}</span>
                        )}
                      </div>
                      <span className="badge badge-yellow">Provisoire</span>
                    </div>
                    <div className="text-sm text-[var(--text2)]">
                      {client?.nom} — {moto?.modele}
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-[var(--text3)]">{ot.vt_vendus} VT · {vtToHM(ot.vt_vendus)}</span>
                      <button className="btn btn-primary btn-sm">Confirmer</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Charge Atelier */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Charge atelier — Aujourd'hui</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setView('planning')}>
              Planning <ChevronRight size={14} />
            </button>
          </div>
          <div className="card-body">
            {techniciens.map(tech => {
              const pct = Math.floor(Math.random() * 80) + 20; // Mock for now
              const color = pct > 85 ? 'var(--red)' : pct > 60 ? 'var(--yellow)' : 'var(--green)';
              return (
                <div key={tech.id} className="flex items-center gap-3 mb-3">
                  <div 
                    className="tech-avatar" 
                    style={{ background: tech.couleur_hex }}
                  >
                    {tech.initiales}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm">
                      <span>{tech.prenom}</span>
                      <span style={{ color }}>{pct}%</span>
                    </div>
                    <div className="charge-bar mt-1">
                      <div 
                        className="charge-fill" 
                        style={{ width: `${pct}%`, background: color }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pipeline */}
      <div className="card mt-4">
        <div className="card-header">
          <span className="card-title">Pipeline — leads actifs</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setView('pipeline')}>
            Complet <ChevronRight size={14} />
          </button>
        </div>
        <div className="card-body">
          <div className="grid-3">
            {leadsActifs.slice(0, 6).map(lead => {
              const client = lead.client_id ? getClient(lead.client_id) : null;
              const stage = PIPELINE_STAGES.find(s => s.key === lead.statut);
              return (
                <div key={lead.id} className="pipeline-card" data-testid={`lead-${lead.id}`}>
                  <div className="font-medium text-sm mb-1">{lead.moto_libelle}</div>
                  <div className="text-xs text-[var(--text3)] mb-2">
                    {client ? `${client.prenom} ${client.nom}` : lead.prospect_nom}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-sm">{fmtCHF(lead.prix_offert)}</span>
                    <span className="badge" style={{ 
                      background: `${stage?.color}22`, 
                      color: stage?.color 
                    }}>
                      {stage?.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Ordres de Travail Component
const OrdresTravail = ({ ots, techniciens, clients, motos, onUpdate, prestations }) => {
  const [filter, setFilter] = useState('tous');
  const [selectedOT, setSelectedOT] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showMotoModal, setShowMotoModal] = useState(false);
  const [showBexioLinkModal, setShowBexioLinkModal] = useState(null);
  const [bexioInvoices, setBexioInvoices] = useState([]);
  const [bexioOffers, setBexioOffers] = useState([]);
  const [loadingBexio, setLoadingBexio] = useState(false);
  const [bexioLinkType, setBexioLinkType] = useState('invoice'); // 'invoice' or 'offer'
  const [newOT, setNewOT] = useState({
    client_id: '',
    moto_id: '',
    technicien_id: '',
    date_planifiee: '',
    description: '',
    lignes: []
  });
  const [newClient, setNewClient] = useState({ nom: '', prenom: '', email: '', tel: '', ville: '' });
  const [newMoto, setNewMoto] = useState({ client_id: '', marque: 'BMW', modele: '', vin: '', immatriculation: '', km_actuel: 0 });
  const [creating, setCreating] = useState(false);

  const filteredOTs = filter === 'tous' ? ots : ots.filter(o => o.statut === filter);
  
  const getClient = (id) => clients.find(c => c.id === id);
  const getMoto = (id) => motos.find(m => m.id === id);
  const getTech = (id) => techniciens.find(t => t.id === id);
  
  // Motos du client sélectionné
  const clientMotos = motos.filter(m => m.client_id === newOT.client_id);

  // Open Bexio link modal and load invoices + offers
  const openBexioLinkModal = async (ot, type = 'invoice') => {
    setShowBexioLinkModal(ot);
    setBexioLinkType(type);
    setLoadingBexio(true);
    try {
      const [invoicesRes, offersRes] = await Promise.all([
        bexioAPI.getLocalInvoices(100),
        bexioAPI.getLocalOffers(100)
      ]);
      setBexioInvoices(invoicesRes.data);
      setBexioOffers(offersRes.data);
    } catch (err) {
      console.error('Error loading Bexio data:', err);
    } finally {
      setLoadingBexio(false);
    }
  };

  // Link invoice to OT (reference becomes invoice number)
  const handleLinkInvoice = async (invoiceId) => {
    try {
      await bexioAPI.linkInvoiceToOT(showBexioLinkModal.id, invoiceId);
      onUpdate();
      setShowBexioLinkModal(null);
    } catch (err) {
      alert('Erreur lors de la liaison');
    }
  };

  // Link offer to OT (reference becomes offer number)
  const handleLinkOffer = async (offerId) => {
    try {
      await bexioAPI.linkOfferToOT(showBexioLinkModal.id, offerId);
      onUpdate();
      setShowBexioLinkModal(null);
    } catch (err) {
      alert('Erreur lors de la liaison');
    }
  };

  const handleConfirm = async (otId) => {
    try {
      await ordresTravailAPI.update(otId, { statut: 'confirme' });
      onUpdate();
      setSelectedOT(null);
    } catch (err) {
      console.error('Error confirming OT:', err);
    }
  };

  const handleCreateOT = async () => {
    if (!newOT.client_id || !newOT.moto_id) {
      alert('Veuillez sélectionner un client et une moto');
      return;
    }
    setCreating(true);
    try {
      await ordresTravailAPI.create(newOT);
      onUpdate();
      setShowCreateModal(false);
      setNewOT({ client_id: '', moto_id: '', technicien_id: '', date_planifiee: '', description: '', lignes: [] });
    } catch (err) {
      console.error('Error creating OT:', err);
      alert('Erreur lors de la création de l\'OT');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.nom) {
      alert('Le nom est obligatoire');
      return;
    }
    setCreating(true);
    try {
      const res = await clientsAPI.create(newClient);
      onUpdate();
      setShowClientModal(false);
      setNewClient({ nom: '', prenom: '', email: '', tel: '', ville: '' });
      // Sélectionner automatiquement le nouveau client
      setNewOT(prev => ({ ...prev, client_id: res.data.id }));
    } catch (err) {
      console.error('Error creating client:', err);
      alert('Erreur lors de la création du client');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateMoto = async () => {
    if (!newMoto.client_id || !newMoto.modele) {
      alert('Client et modèle sont obligatoires');
      return;
    }
    setCreating(true);
    try {
      const res = await motosAPI.create(newMoto);
      onUpdate();
      setShowMotoModal(false);
      setNewMoto({ client_id: '', marque: 'BMW', modele: '', vin: '', immatriculation: '', km_actuel: 0 });
      // Sélectionner automatiquement la nouvelle moto
      setNewOT(prev => ({ ...prev, moto_id: res.data.id }));
    } catch (err) {
      console.error('Error creating moto:', err);
      alert('Erreur lors de la création de la moto');
    } finally {
      setCreating(false);
    }
  };

  const addLigneFromPrestation = (prestation) => {
    const newLigne = {
      id: `temp-${Date.now()}`,
      libelle: prestation.libelle,
      vt_vendus: prestation.vt_standard,
      vt_realises: 0,
      manuelle: false
    };
    setNewOT(prev => ({ ...prev, lignes: [...prev.lignes, newLigne] }));
  };

  const removeLigne = (ligneId) => {
    setNewOT(prev => ({ ...prev, lignes: prev.lignes.filter(l => l.id !== ligneId) }));
  };

  const totalVTVendus = newOT.lignes.reduce((sum, l) => sum + (l.vt_vendus || 0), 0);

  return (
    <div data-testid="ot-view">
      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">VT planifiés actifs</div>
          <div className="stat-value">
            {ots.filter(o => !['annule', 'termine'].includes(o.statut)).reduce((s, o) => s + (o.vt_planifies || 0), 0)}
          </div>
          <div className="stat-sub">
            {vtToHM(ots.filter(o => !['annule', 'termine'].includes(o.statut)).reduce((s, o) => s + (o.vt_planifies || 0), 0))}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">OT provisoires</div>
          <div className="stat-value" style={{ color: 'var(--yellow)' }}>
            {ots.filter(o => o.statut === 'provisoire').length}
          </div>
          <div className="stat-sub">En attente confirmation</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">OT confirmés</div>
          <div className="stat-value" style={{ color: 'var(--green)' }}>
            {ots.filter(o => o.statut === 'confirme').length}
          </div>
          <div className="stat-sub">Planning bloqué</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Rendement moyen</div>
          <div className="stat-value">
            {(() => {
              const termines = ots.filter(o => o.statut === 'termine' && o.vt_realises > 0);
              if (termines.length === 0) return '—';
              const avg = termines.reduce((s, o) => s + (o.vt_vendus / o.vt_realises), 0) / termines.length;
              return Math.round(avg * 100) + '%';
            })()}
          </div>
          <div className="stat-sub">OT terminés</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex justify-between items-center mb-4">
        <div className="filter-tabs">
          {['tous', 'provisoire', 'confirme', 'en_cours', 'termine'].map(f => (
            <button
              key={f}
              className={`filter-tab ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
            >
              {f === 'tous' ? 'Tous' : OT_STATUTS[f]?.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button 
            className="btn btn-ghost"
            onClick={async () => {
              try {
                const res = await exportAPI.ordresTravail({ statut: filter !== 'tous' ? filter : undefined });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `ordres_travail_${new Date().toISOString().split('T')[0]}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
              } catch (err) {
                alert('Erreur export CSV');
              }
            }}
            data-testid="export-ot-csv-btn"
          >
            <Download size={14} /> Export CSV
          </button>
          <button className="btn btn-ghost">↕ Import Bexio</button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-ot-btn"
          >
            <Plus size={14} /> Nouvel OT
          </button>
        </div>
      </div>

      {/* OT List */}
      <div>
        {filteredOTs.map(ot => {
          const client = getClient(ot.client_id);
          const moto = getMoto(ot.moto_id);
          const tech = getTech(ot.technicien_id);
          const rend = ot.vt_realises > 0 ? Math.round((ot.vt_vendus / ot.vt_realises) * 100) : null;

          return (
            <div 
              key={ot.id} 
              className={`ot-row ${ot.statut === 'provisoire' ? 'provisoire' : ''}`}
              onClick={() => setSelectedOT(ot)}
              data-testid={`ot-row-${ot.id}`}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  {/* Référence OT avec style différent selon le type */}
                  {ot.reference?.startsWith('BROUILLON') ? (
                    <span className="font-mono text-sm px-2 py-0.5 rounded bg-[var(--orange)]/20 text-[var(--orange)]">
                      {ot.reference}
                    </span>
                  ) : (
                    <span className="font-condensed font-bold text-lg text-[var(--accent)]">
                      {ot.reference}
                    </span>
                  )}
                  {/* Indicateur du type Bexio */}
                  {ot.bexio_offre_ref && !ot.reference?.startsWith('BROUILLON') && (
                    <span className="text-xs text-[var(--blue)] bg-[var(--blue)]/10 px-2 py-0.5 rounded">Offre</span>
                  )}
                  {ot.bexio_facture_ref && !ot.reference?.startsWith('BROUILLON') && (
                    <span className="text-xs text-[var(--green)] bg-[var(--green)]/10 px-2 py-0.5 rounded">Facture</span>
                  )}
                  {ot.assigne_auto && (
                    <span className="text-xs text-[var(--text3)] ml-1">⚡ Auto</span>
                  )}
                </div>
                <span className={`badge ${OT_STATUTS[ot.statut]?.cls}`}>
                  {OT_STATUTS[ot.statut]?.label}
                </span>
              </div>
              
              <div className="text-sm text-[var(--text2)] mb-1">
                {client?.prenom} {client?.nom} — {moto?.marque} {moto?.modele} {moto?.annee}
              </div>
              <div className="text-xs text-[var(--text3)] mb-3">{ot.description}</div>

              <div className="flex items-center gap-6 text-sm">
                <div className="text-[var(--text3)]">{fmtDate(ot.date_planifiee)}</div>
                {tech && (
                  <div className="flex items-center gap-2">
                    <div className="tech-avatar" style={{ background: tech.couleur_hex, width: 20, height: 20, fontSize: 8 }}>
                      {tech.initiales}
                    </div>
                    <span className="text-[var(--text2)]">{tech.prenom}</span>
                  </div>
                )}
                <div>
                  <span className="font-condensed font-bold text-[var(--accent)]">{ot.vt_vendus}</span>
                  <span className="text-[var(--text3)] text-xs ml-1">VT vendus</span>
                  <span className="text-[var(--text3)] text-xs ml-2">{vtToHM(ot.vt_vendus)}</span>
                </div>
                {rend && (
                  <span className="badge" style={{
                    background: rend >= 100 ? 'var(--red-dim)' : 'var(--green-dim)',
                    color: rend >= 100 ? 'var(--red)' : 'var(--green)'
                  }}>
                    {rend}% rend.
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* OT Detail Panel */}
      {selectedOT && (
        <div className="panel-overlay" onClick={() => setSelectedOT(null)}>
          <div className="panel" onClick={e => e.stopPropagation()} data-testid="ot-panel">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="panel-title">{selectedOT.reference}</div>
                <div className="flex items-center gap-2 mt-2">
                  {selectedOT.bexio_offre_ref && (
                    <span className="badge badge-orange">⬡ {selectedOT.bexio_offre_ref}</span>
                  )}
                  <span className={`badge ${OT_STATUTS[selectedOT.statut]?.cls}`}>
                    {OT_STATUTS[selectedOT.statut]?.label}
                  </span>
                </div>
              </div>
              <button className="panel-close" onClick={() => setSelectedOT(null)}>
                <X size={20} />
              </button>
            </div>

            {/* VT Summary */}
            <div className="grid-3 mb-4">
              {[
                { label: 'Vendus', val: selectedOT.vt_vendus, color: 'var(--accent)' },
                { label: 'Planifiés', val: selectedOT.vt_planifies, color: 'var(--yellow)' },
                { label: 'Réalisés', val: selectedOT.vt_realises, color: selectedOT.vt_realises > selectedOT.vt_vendus ? 'var(--red)' : 'var(--green)' },
              ].map(x => (
                <div key={x.label} className="stat-card" style={{ borderTopColor: x.color }}>
                  <div className="stat-value" style={{ color: x.color, fontSize: 24 }}>{x.val || '—'}</div>
                  <div className="stat-label">{x.label}</div>
                  <div className="stat-sub">{x.val ? vtToHM(x.val) : '–'}</div>
                </div>
              ))}
            </div>

            {/* Client & Moto Info */}
            <div className="panel-section">
              <div className="panel-section-title">Intervention</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--text3)]">Client</span>
                  <span>{getClient(selectedOT.client_id)?.prenom} {getClient(selectedOT.client_id)?.nom}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text3)]">Moto</span>
                  <span>{getMoto(selectedOT.moto_id)?.marque} {getMoto(selectedOT.moto_id)?.modele}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text3)]">Km entrée</span>
                  <span className="font-mono">{selectedOT.km_entree?.toLocaleString()} km</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text3)]">Date</span>
                  <span>{fmtDate(selectedOT.date_planifiee)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text3)]">Technicien</span>
                  <span>{getTech(selectedOT.technicien_id)?.prenom || '—'}</span>
                </div>
              </div>
            </div>

            {/* Lignes */}
            <div className="panel-section">
              <div className="panel-section-title">Lignes OT</div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Prestation</th>
                    <th>VT vendus</th>
                    <th>VT réalisés</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedOT.lignes || []).map(l => (
                    <tr key={l.id}>
                      <td>
                        {l.libelle}
                        {l.manuelle && <span className="badge badge-gray ml-2">Manuel</span>}
                      </td>
                      <td className="font-mono">{l.vt_vendus}</td>
                      <td className="font-mono">{l.vt_realises}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div className="flex gap-2 mt-6 flex-wrap">
              {selectedOT.statut === 'provisoire' && (
                <button 
                  className="btn btn-primary"
                  onClick={() => handleConfirm(selectedOT.id)}
                  data-testid="confirm-ot-btn"
                >
                  <Check size={14} /> Confirmer l'OT
                </button>
              )}
              {/* Boutons Lier Bexio - visibles si référence est BROUILLON */}
              {selectedOT.reference?.startsWith('BROUILLON') && !selectedOT.bexio_offre_ref && !selectedOT.bexio_facture_ref && (
                <>
                  <button 
                    className="btn btn-ghost"
                    onClick={() => openBexioLinkModal(selectedOT, 'offer')}
                    data-testid="link-bexio-offer-btn"
                  >
                    <FileText size={14} /> Lier offre Bexio
                  </button>
                  <button 
                    className="btn btn-ghost"
                    onClick={() => openBexioLinkModal(selectedOT, 'invoice')}
                    data-testid="link-bexio-invoice-btn"
                  >
                    <RefreshCw size={14} /> Lier facture Bexio
                  </button>
                </>
              )}
              {/* Affichage référence Bexio si liée */}
              {selectedOT.bexio_offre_ref && (
                <span className="flex items-center gap-2 text-[var(--blue)] text-sm bg-[var(--blue)]/10 px-3 py-1.5 rounded-lg">
                  <FileText size={14} /> Offre: {selectedOT.bexio_offre_ref}
                </span>
              )}
              {selectedOT.bexio_facture_ref && (
                <span className="flex items-center gap-2 text-[var(--green)] text-sm bg-[var(--green)]/10 px-3 py-1.5 rounded-lg">
                  <Check size={14} /> Facture: {selectedOT.bexio_facture_ref}
                </span>
              )}
              <button className="btn btn-ghost">Ouvrir Bexio</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Lier Bexio (Offre ou Facture) */}
      {showBexioLinkModal && (
        <div className="panel-overlay" onClick={() => setShowBexioLinkModal(null)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '600px' }}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="panel-title">
                  Lier {bexioLinkType === 'offer' ? 'une offre' : 'une facture'} Bexio
                </div>
                <div className="text-sm text-[var(--text3)] mt-1">
                  OT actuel: <span className="font-mono">{showBexioLinkModal.reference}</span>
                </div>
                <div className="text-xs text-[var(--text3)] mt-1">
                  La référence OT deviendra le numéro Bexio sélectionné
                </div>
              </div>
              <button className="panel-close" onClick={() => setShowBexioLinkModal(null)}>
                <X size={20} />
              </button>
            </div>

            {/* Toggle Offre / Facture */}
            <div className="flex gap-2 mb-4 p-1 bg-[var(--bg2)] rounded-lg w-fit">
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  bexioLinkType === 'offer' 
                    ? 'bg-[var(--blue)] text-white' 
                    : 'text-[var(--text2)] hover:text-[var(--text1)]'
                }`}
                onClick={() => setBexioLinkType('offer')}
                data-testid="toggle-offer-btn"
              >
                <FileText size={14} className="inline mr-2" />
                Offres ({bexioOffers.length})
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  bexioLinkType === 'invoice' 
                    ? 'bg-[var(--green)] text-white' 
                    : 'text-[var(--text2)] hover:text-[var(--text1)]'
                }`}
                onClick={() => setBexioLinkType('invoice')}
                data-testid="toggle-invoice-btn"
              >
                <RefreshCw size={14} className="inline mr-2" />
                Factures ({bexioInvoices.length})
              </button>
            </div>

            {loadingBexio ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--accent)]" />
              </div>
            ) : bexioLinkType === 'offer' ? (
              /* Liste des Offres */
              bexioOffers.length === 0 ? (
                <div className="text-center py-8 text-[var(--text3)]">
                  <p className="mb-3">Aucune offre synchronisée.</p>
                  <p className="text-xs">Allez dans Bexio Sync pour synchroniser les offres.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {bexioOffers.map(offer => (
                    <div 
                      key={offer.bexio_id}
                      className="p-3 rounded-lg border border-[var(--border)] hover:border-[var(--blue)] cursor-pointer transition-colors"
                      onClick={() => handleLinkOffer(offer.bexio_id)}
                      data-testid={`bexio-offer-${offer.bexio_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-mono font-bold text-[var(--blue)]">{offer.document_nr}</div>
                          <div className="text-sm text-[var(--text2)]">{offer.title || 'Sans titre'}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono">{fmtCHF(offer.total)}</div>
                          <div className="text-xs text-[var(--text3)]">{offer.is_valid_from ? fmtDate(offer.is_valid_from) : ''}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              /* Liste des Factures */
              bexioInvoices.length === 0 ? (
                <div className="text-center py-8 text-[var(--text3)]">
                  <p className="mb-3">Aucune facture synchronisée.</p>
                  <p className="text-xs">Allez dans Bexio Sync pour synchroniser les factures.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {bexioInvoices.map(inv => (
                    <div 
                      key={inv.bexio_id}
                      className="p-3 rounded-lg border border-[var(--border)] hover:border-[var(--green)] cursor-pointer transition-colors"
                      onClick={() => handleLinkInvoice(inv.bexio_id)}
                      data-testid={`bexio-invoice-${inv.bexio_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-mono font-bold text-[var(--green)]">{inv.document_nr}</div>
                          <div className="text-sm text-[var(--text2)]">{inv.title || 'Sans titre'}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono">{fmtCHF(inv.total)}</div>
                          <div className="text-xs text-[var(--text3)]">{inv.is_valid_from ? fmtDate(inv.is_valid_from) : ''}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Modal Création OT */}
      {showCreateModal && (
        <div className="panel-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="panel-slide" style={{ maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
            <div className="panel-header">
              <h2 className="panel-title">Nouvel Ordre de Travail</h2>
              <button className="panel-close" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="panel-body">
              {/* Client */}
              <div className="panel-section">
                <div className="panel-section-title flex justify-between items-center">
                  <span>Client</span>
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowClientModal(true)}
                  >
                    <Plus size={14} /> Nouveau client
                  </button>
                </div>
                <select 
                  className="input w-full"
                  value={newOT.client_id}
                  onChange={(e) => setNewOT(prev => ({ ...prev, client_id: e.target.value, moto_id: '' }))}
                  data-testid="ot-client-select"
                >
                  <option value="">-- Sélectionner un client --</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.prenom} {c.nom} - {c.ville}</option>
                  ))}
                </select>
              </div>

              {/* Moto */}
              {newOT.client_id && (
                <div className="panel-section">
                  <div className="panel-section-title flex justify-between items-center">
                    <span>Moto</span>
                    <button 
                      className="btn btn-ghost btn-sm"
                      onClick={() => { setNewMoto(prev => ({ ...prev, client_id: newOT.client_id })); setShowMotoModal(true); }}
                    >
                      <Plus size={14} /> Nouvelle moto
                    </button>
                  </div>
                  {clientMotos.length === 0 ? (
                    <p className="text-sm text-[var(--text3)]">Ce client n'a pas de moto enregistrée</p>
                  ) : (
                    <select 
                      className="input w-full"
                      value={newOT.moto_id}
                      onChange={(e) => setNewOT(prev => ({ ...prev, moto_id: e.target.value }))}
                      data-testid="ot-moto-select"
                    >
                      <option value="">-- Sélectionner une moto --</option>
                      {clientMotos.map(m => (
                        <option key={m.id} value={m.id}>{m.marque} {m.modele} - {m.immatriculation}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Date et Technicien */}
              <div className="panel-section">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[var(--text3)] mb-1">Date planifiée</label>
                    <input 
                      type="date"
                      className="input w-full"
                      value={newOT.date_planifiee}
                      onChange={(e) => setNewOT(prev => ({ ...prev, date_planifiee: e.target.value }))}
                      data-testid="ot-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text3)] mb-1">Technicien</label>
                    <select 
                      className="input w-full"
                      value={newOT.technicien_id}
                      onChange={(e) => setNewOT(prev => ({ ...prev, technicien_id: e.target.value }))}
                      data-testid="ot-tech-select"
                    >
                      <option value="">-- Auto-assignation --</option>
                      {techniciens.map(t => (
                        <option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="panel-section">
                <label className="block text-sm text-[var(--text3)] mb-1">Description</label>
                <textarea 
                  className="input w-full"
                  rows={2}
                  placeholder="Description des travaux..."
                  value={newOT.description}
                  onChange={(e) => setNewOT(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="ot-description-input"
                />
              </div>

              {/* Prestations */}
              <div className="panel-section">
                <div className="panel-section-title">Prestations</div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {(prestations || []).slice(0, 8).map(p => (
                    <button 
                      key={p.id}
                      className="btn btn-ghost btn-sm text-left justify-start"
                      onClick={() => addLigneFromPrestation(p)}
                    >
                      <Plus size={12} /> {p.libelle} ({p.vt_standard} VT)
                    </button>
                  ))}
                </div>

                {/* Lignes ajoutées */}
                {newOT.lignes.length > 0 && (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Prestation</th>
                        <th className="text-right">VT</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {newOT.lignes.map(l => (
                        <tr key={l.id}>
                          <td>{l.libelle}</td>
                          <td className="text-right font-mono">{l.vt_vendus}</td>
                          <td className="text-right">
                            <button 
                              className="btn btn-ghost btn-sm text-red-500"
                              onClick={() => removeLigne(l.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className="font-bold">
                        <td>Total</td>
                        <td className="text-right font-mono">{totalVTVendus} VT</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 mt-6">
                <button className="btn btn-ghost" onClick={() => setShowCreateModal(false)}>Annuler</button>
                <button 
                  className="btn btn-primary"
                  onClick={handleCreateOT}
                  disabled={creating || !newOT.client_id || !newOT.moto_id}
                  data-testid="submit-ot-btn"
                >
                  {creating ? <RefreshCw className="animate-spin" size={14} /> : <Check size={14} />}
                  Créer l'OT
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Création Client */}
      {showClientModal && (
        <div className="panel-overlay" onClick={() => setShowClientModal(false)}>
          <div className="panel-slide" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="panel-header">
              <h2 className="panel-title">Nouveau Client</h2>
              <button className="panel-close" onClick={() => setShowClientModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="panel-body">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[var(--text3)] mb-1">Prénom</label>
                    <input 
                      type="text"
                      className="input w-full"
                      value={newClient.prenom}
                      onChange={(e) => setNewClient(prev => ({ ...prev, prenom: e.target.value }))}
                      data-testid="client-prenom-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text3)] mb-1">Nom *</label>
                    <input 
                      type="text"
                      className="input w-full"
                      value={newClient.nom}
                      onChange={(e) => setNewClient(prev => ({ ...prev, nom: e.target.value }))}
                      data-testid="client-nom-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[var(--text3)] mb-1">Email</label>
                  <input 
                    type="email"
                    className="input w-full"
                    value={newClient.email}
                    onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                    data-testid="client-email-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[var(--text3)] mb-1">Téléphone</label>
                    <input 
                      type="tel"
                      className="input w-full"
                      value={newClient.tel}
                      onChange={(e) => setNewClient(prev => ({ ...prev, tel: e.target.value }))}
                      data-testid="client-tel-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text3)] mb-1">Ville</label>
                    <input 
                      type="text"
                      className="input w-full"
                      value={newClient.ville}
                      onChange={(e) => setNewClient(prev => ({ ...prev, ville: e.target.value }))}
                      data-testid="client-ville-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button className="btn btn-ghost" onClick={() => setShowClientModal(false)}>Annuler</button>
                <button 
                  className="btn btn-primary"
                  onClick={handleCreateClient}
                  disabled={creating || !newClient.nom}
                  data-testid="submit-client-btn"
                >
                  {creating ? <RefreshCw className="animate-spin" size={14} /> : <Check size={14} />}
                  Créer le client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Création Moto */}
      {showMotoModal && (
        <div className="panel-overlay" onClick={() => setShowMotoModal(false)}>
          <div className="panel-slide" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="panel-header">
              <h2 className="panel-title">Nouvelle Moto</h2>
              <button className="panel-close" onClick={() => setShowMotoModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="panel-body">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[var(--text3)] mb-1">Client *</label>
                  <select 
                    className="input w-full"
                    value={newMoto.client_id}
                    onChange={(e) => setNewMoto(prev => ({ ...prev, client_id: e.target.value }))}
                    data-testid="moto-client-select"
                  >
                    <option value="">-- Sélectionner --</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[var(--text3)] mb-1">Marque</label>
                    <select 
                      className="input w-full"
                      value={newMoto.marque}
                      onChange={(e) => setNewMoto(prev => ({ ...prev, marque: e.target.value }))}
                      data-testid="moto-marque-select"
                    >
                      <option value="BMW">BMW</option>
                      <option value="Ducati">Ducati</option>
                      <option value="Honda">Honda</option>
                      <option value="Kawasaki">Kawasaki</option>
                      <option value="Yamaha">Yamaha</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text3)] mb-1">Modèle *</label>
                    <input 
                      type="text"
                      className="input w-full"
                      placeholder="R 1250 GS"
                      value={newMoto.modele}
                      onChange={(e) => setNewMoto(prev => ({ ...prev, modele: e.target.value }))}
                      data-testid="moto-modele-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[var(--text3)] mb-1">VIN</label>
                    <input 
                      type="text"
                      className="input w-full"
                      placeholder="WB10..."
                      value={newMoto.vin}
                      onChange={(e) => setNewMoto(prev => ({ ...prev, vin: e.target.value }))}
                      data-testid="moto-vin-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text3)] mb-1">Immatriculation</label>
                    <input 
                      type="text"
                      className="input w-full"
                      placeholder="NE 12345"
                      value={newMoto.immatriculation}
                      onChange={(e) => setNewMoto(prev => ({ ...prev, immatriculation: e.target.value }))}
                      data-testid="moto-immat-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[var(--text3)] mb-1">Kilométrage actuel</label>
                  <input 
                    type="number"
                    className="input w-full"
                    value={newMoto.km_actuel}
                    onChange={(e) => setNewMoto(prev => ({ ...prev, km_actuel: parseInt(e.target.value) || 0 }))}
                    data-testid="moto-km-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button className="btn btn-ghost" onClick={() => setShowMotoModal(false)}>Annuler</button>
                <button 
                  className="btn btn-primary"
                  onClick={handleCreateMoto}
                  disabled={creating || !newMoto.client_id || !newMoto.modele}
                  data-testid="submit-moto-btn"
                >
                  {creating ? <RefreshCw className="animate-spin" size={14} /> : <Check size={14} />}
                  Créer la moto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Pipeline Component
const Pipeline = ({ leads, clients }) => {
  const getClient = (id) => clients.find(c => c.id === id);
  
  const pipelineTotal = leads
    .filter(l => !['gagne', 'perdu'].includes(l.statut))
    .reduce((s, l) => s + (l.prix_offert || 0), 0);

  return (
    <div data-testid="pipeline-view">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="font-condensed text-3xl font-bold">{fmtCHF(pipelineTotal)}</div>
          <div className="text-sm text-[var(--text3)]">pipeline actif</div>
        </div>
        <button className="btn btn-primary">+ Nouveau lead</button>
      </div>

      {/* Kanban Board */}
      <div className="pipeline-board">
        {PIPELINE_STAGES.map(stage => {
          const stageLeads = leads.filter(l => l.statut === stage.key);
          const stageTotal = stageLeads.reduce((s, l) => s + (l.prix_offert || 0), 0);
          
          return (
            <div key={stage.key} className="pipeline-column" data-testid={`pipeline-${stage.key}`}>
              <div className="pipeline-column-header" style={{ borderTop: `3px solid ${stage.color}` }}>
                <span className="pipeline-column-title">{stage.label}</span>
                <span className="badge badge-gray">{stageLeads.length}</span>
              </div>
              <div className="pipeline-column-body">
                {stageLeads.map(lead => {
                  const client = lead.client_id ? getClient(lead.client_id) : null;
                  return (
                    <div key={lead.id} className="pipeline-card" data-testid={`lead-card-${lead.id}`}>
                      <div className="font-medium text-sm mb-1">{lead.moto_libelle}</div>
                      <div className="text-xs text-[var(--text3)] mb-2">
                        {client ? `${client.prenom} ${client.nom}` : lead.prospect_nom || 'Prospect'}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-mono text-sm font-bold">{fmtCHF(lead.prix_offert)}</span>
                        {lead.bexio_devis_ref && (
                          <span className="text-xs text-[var(--accent)]">⬡ {lead.bexio_devis_ref}</span>
                        )}
                      </div>
                      {lead.priorite === 'haute' && (
                        <div className="mt-2">
                          <span className="badge badge-red">Haute priorité</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {stageLeads.length === 0 && (
                  <div className="text-center text-[var(--text3)] text-xs py-4">
                    Aucun lead
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Equipe Component
// Equipe Component - Enhanced with absences and vacation management
const Equipe = ({ techniciens, onUpdate }) => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [view, setView] = useState('equipe'); // equipe, absences, soldes
  const [absences, setAbsences] = useState([]);
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(null); // technicien for PIN verification
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [soldesEdit, setSoldesEdit] = useState({});
  const [newEmployee, setNewEmployee] = useState({
    prenom: '',
    nom: '',
    initiales: '',
    email: '',
    role: 'technicien',
    couleur_hex: '#4CAF50',
    vt_jour_max: 96,
    taux_activite: 100
  });
  const [absenceForm, setAbsenceForm] = useState({
    technicien_id: '',
    type: 'conge',
    debut: '',
    fin: '',
    note: ''
  });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const ROLE_OPTIONS = [
    { key: 'admin', label: 'Direction / Admin', color: '#FF6B35' },
    { key: 'vendeur', label: 'Vendeur', color: '#9B59B6' },
    { key: 'conseiller_sav', label: 'Conseiller SAV', color: '#3498DB' },
    { key: 'technicien', label: 'Technicien / Mécanicien', color: '#4CAF50' },
  ];

  const handleUnlock = async () => {
    try {
      const res = await adminAPI.verifyPin(pinInput);
      if (res.data.valid) {
        setIsUnlocked(true);
        setPinError('');
        setPinInput('');
      } else {
        setPinError('PIN incorrect');
      }
    } catch (err) {
      setPinError('Erreur de vérification');
    }
  };

  // Load absences
  useEffect(() => {
    const loadAbsences = async () => {
      try {
        const res = await absencesAPI.getAll();
        setAbsences(res.data);
      } catch (err) {
        console.error('Error loading absences:', err);
      }
    };
    loadAbsences();
  }, []);

  const handleCreateAbsence = async () => {
    try {
      await absencesAPI.create(absenceForm);
      const res = await absencesAPI.getAll();
      setAbsences(res.data);
      setShowAbsenceModal(false);
      setAbsenceForm({ technicien_id: '', type: 'conge', debut: '', fin: '', note: '' });
      onUpdate();
    } catch (err) {
      console.error('Error creating absence:', err);
    }
  };

  const handleDeleteAbsence = async (absenceId) => {
    try {
      await absencesAPI.delete(absenceId);
      setAbsences(prev => prev.filter(a => a.id !== absenceId));
      onUpdate();
    } catch (err) {
      console.error('Error deleting absence:', err);
    }
  };

  const handleVerifyPin = async () => {
    // Plus besoin de vérifier le PIN ici car l'onglet est déjà déverrouillé
    try {
      const tech = showPinModal;
      await techniciensAPI.updateSoldes(tech.id, soldesEdit[tech.id], '1234'); // PIN déjà vérifié
      setShowPinModal(null);
      onUpdate();
    } catch (err) {
      console.error('Erreur de sauvegarde');
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.prenom || !newEmployee.nom || !newEmployee.initiales) {
      alert('Prénom, nom et initiales sont obligatoires');
      return;
    }
    try {
      await techniciensAPI.create(newEmployee);
      setShowAddEmployeeModal(false);
      setNewEmployee({
        prenom: '',
        nom: '',
        initiales: '',
        email: '',
        role: 'technicien',
        couleur_hex: '#4CAF50',
        vt_jour_max: 96
      });
      onUpdate();
    } catch (err) {
      console.error('Error creating employee:', err);
      alert('Erreur lors de la création');
    }
  };

  const handleDeleteEmployee = async (tech) => {
    // PIN déjà vérifié à l'entrée de l'onglet
    try {
      await techniciensAPI.delete(tech.id, '1234');
      setShowDeleteConfirm(null);
      onUpdate();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleSaveSoldes = async (tech) => {
    try {
      await techniciensAPI.updateSoldes(tech.id, soldesEdit[tech.id], '1234');
      setSoldesEdit(prev => {
        const newState = { ...prev };
        delete newState[tech.id];
        return newState;
      });
      onUpdate();
    } catch (err) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  const getTech = (id) => techniciens.find(t => t.id === id);

  const TYPE_ABSENCE = [
    { key: 'conge', label: 'Congé', icon: Palmtree, color: 'var(--blue)' },
    { key: 'maladie', label: 'Maladie', icon: Thermometer, color: 'var(--red)' },
    { key: 'formation', label: 'Formation', icon: GraduationCap, color: 'var(--accent)' },
    { key: 'autre', label: 'Autre', icon: Calendar, color: 'var(--text3)' },
  ];

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  // Écran de verrouillage
  if (!isUnlocked) {
    return (
      <div data-testid="equipe-locked" className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <div className="card" style={{ width: '400px' }}>
          <div className="card-body text-center">
            <div className="mb-6">
              <Lock size={48} className="mx-auto mb-4" style={{ color: 'var(--accent)' }} />
              <h2 className="text-xl font-bold mb-2">Équipe & Absences</h2>
              <p className="text-sm text-[var(--text3)]">
                Cette section est protégée.<br/>
                Entrez le PIN admin pour accéder.
              </p>
            </div>
            
            <div className="space-y-4">
              <input
                type="password"
                className="input w-full text-center text-2xl tracking-widest"
                maxLength={4}
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && pinInput.length === 4 && handleUnlock()}
                placeholder="••••"
                autoFocus
                data-testid="pin-input"
              />
              {pinError && (
                <p className="text-red-500 text-sm">{pinError}</p>
              )}
              <button 
                className="btn btn-primary w-full"
                onClick={handleUnlock}
                disabled={pinInput.length !== 4}
                data-testid="pin-submit"
              >
                <Lock size={14} /> Déverrouiller
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="equipe-view">
      {/* Tabs */}
      <div className="flex justify-between items-center mb-6">
        <div className="filter-tabs">
          <button className={`filter-tab ${view === 'equipe' ? 'active' : ''}`} onClick={() => setView('equipe')}>
            <Users size={14} /> Équipe
          </button>
          <button className={`filter-tab ${view === 'absences' ? 'active' : ''}`} onClick={() => setView('absences')}>
            <Calendar size={14} /> Absences
            {absences.length > 0 && <span className="nav-badge ml-2">{absences.length}</span>}
          </button>
          <button className={`filter-tab ${view === 'soldes' ? 'active' : ''}`} onClick={() => setView('soldes')}>
            <Palmtree size={14} /> Soldes vacances
          </button>
        </div>
        {view === 'absences' && (
          <button className="btn btn-primary" onClick={() => setShowAbsenceModal(true)}>
            <Plus size={14} /> Nouvelle absence
          </button>
        )}
      </div>

      {/* Equipe view */}
      {view === 'equipe' && (
        <div>
          {/* Admin / Vendeurs */}
          <h3 className="text-sm font-bold uppercase text-[var(--text3)] mb-3">Direction & Ventes</h3>
          <div className="grid-3 mb-6">
            {techniciens.filter(t => t.role === 'admin' || t.role === 'vendeur').map(tech => {
              const techAbsences = absences.filter(a => a.technicien_id === tech.id);
              const tauxActivite = tech.taux_activite || 100;
              const joursVacancesProrata = Math.round((tech.jours_vacances_total || 25) * tauxActivite / 100 * 10) / 10;
              const vacancesRestantes = joursVacancesProrata - (tech.jours_vacances_pris || 0);
              
              return (
                <div key={tech.id} className="card card-accent">
                  <div className="card-body">
                    <div className="flex items-center gap-4 mb-4">
                      <div 
                        className="tech-avatar" 
                        style={{ background: tech.couleur_hex, width: 48, height: 48, fontSize: 16 }}
                      >
                        {tech.initiales}
                      </div>
                      <div>
                        <div className="font-bold text-lg">{tech.prenom} {tech.nom}</div>
                        <div className="text-sm text-[var(--accent)]">
                          {tech.role === 'admin' ? 'Direction' : 'Vendeur'}
                          {tauxActivite < 100 && <span className="ml-2 text-[var(--text3)]">({tauxActivite}%)</span>}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      {tech.email && (
                        <div className="flex justify-between">
                          <span className="text-[var(--text3)]">Email</span>
                          <span className="text-xs">{tech.email}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-[var(--text3)]">Taux d'activité</span>
                        <span className="font-mono font-bold">{tauxActivite}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--text3)]">Vacances (prorata)</span>
                        <span className="font-mono" style={{ color: vacancesRestantes < 5 ? 'var(--red)' : 'var(--green)' }}>
                          {vacancesRestantes} / {joursVacancesProrata} jours
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--text3)]">Jours maladie</span>
                        <span className="font-mono">{tech.jours_maladie || 0} jours</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Conseiller SAV */}
          <h3 className="text-sm font-bold uppercase text-[var(--text3)] mb-3">Service Après-Vente</h3>
          <div className="grid-3 mb-6">
            {techniciens.filter(t => t.role === 'conseiller_sav').map(tech => {
              const techAbsences = absences.filter(a => a.technicien_id === tech.id);
              const tauxActivite = tech.taux_activite || 100;
              const joursVacancesProrata = Math.round((tech.jours_vacances_total || 25) * tauxActivite / 100 * 10) / 10;
              const vacancesRestantes = joursVacancesProrata - (tech.jours_vacances_pris || 0);
              
              return (
                <div key={tech.id} className="card card-accent">
                  <div className="card-body">
                    <div className="flex items-center gap-4 mb-4">
                      <div 
                        className="tech-avatar" 
                        style={{ background: tech.couleur_hex, width: 48, height: 48, fontSize: 16 }}
                      >
                        {tech.initiales}
                      </div>
                      <div>
                        <div className="font-bold text-lg">{tech.prenom} {tech.nom}</div>
                        <div className="text-sm" style={{ color: '#3498DB' }}>
                          Conseiller SAV
                          {tauxActivite < 100 && <span className="ml-2 text-[var(--text3)]">({tauxActivite}%)</span>}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      {tech.email && (
                        <div className="flex justify-between">
                          <span className="text-[var(--text3)]">Email</span>
                          <span className="text-xs">{tech.email}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-[var(--text3)]">Taux d'activité</span>
                        <span className="font-mono font-bold">{tauxActivite}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--text3)]">Vacances (prorata)</span>
                        <span className="font-mono" style={{ color: vacancesRestantes < 5 ? 'var(--red)' : 'var(--green)' }}>
                          {vacancesRestantes} / {joursVacancesProrata} jours
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--text3)]">Jours maladie</span>
                        <span className="font-mono">{tech.jours_maladie || 0} jours</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Techniciens Atelier */}
          <h3 className="text-sm font-bold uppercase text-[var(--text3)] mb-3">Atelier</h3>
          <div className="grid-3">
            {techniciens.filter(t => !t.role || t.role === 'technicien').map(tech => {
            const techAbsences = absences.filter(a => a.technicien_id === tech.id);
            const tauxActivite = tech.taux_activite || 100;
            const joursVacancesProrata = Math.round((tech.jours_vacances_total || 25) * tauxActivite / 100 * 10) / 10;
            const vacancesRestantes = joursVacancesProrata - (tech.jours_vacances_pris || 0);
            
            return (
              <div key={tech.id} className="card card-accent">
                <div className="card-body">
                  <div className="flex items-center gap-4 mb-4">
                    <div 
                      className="tech-avatar" 
                      style={{ background: tech.couleur_hex, width: 48, height: 48, fontSize: 16 }}
                    >
                      {tech.initiales}
                    </div>
                    <div>
                      <div className="font-bold text-lg">{tech.prenom} {tech.nom}</div>
                      <div className="text-sm text-[var(--text3)]">
                        Mécanicien
                        {tauxActivite < 100 && <span className="ml-2">({tauxActivite}%)</span>}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--text3)]">Taux d'activité</span>
                      <span className="font-mono font-bold">{tauxActivite}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text3)]">VT max/jour</span>
                      <span className="font-mono">{Math.round(tech.vt_jour_max * tauxActivite / 100)} VT</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text3)]">Capacité</span>
                      <span className="font-mono">{vtToHM(Math.round(tech.vt_jour_max * tauxActivite / 100))}/jour</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text3)]">Vacances (prorata)</span>
                      <span className="font-mono" style={{ color: vacancesRestantes < 5 ? 'var(--red)' : 'var(--green)' }}>
                        {vacancesRestantes} / {joursVacancesProrata} jours
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text3)]">Jours maladie</span>
                      <span className="font-mono">{tech.jours_maladie || 0} jours</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text3)]">Statut</span>
                      <span className={`badge ${tech.actif ? 'badge-green' : 'badge-gray'}`}>
                        {tech.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Upcoming absences */}
                  {techAbsences.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[var(--border)]">
                      <div className="text-xs text-[var(--text3)] uppercase mb-2">Absences planifiées</div>
                      {techAbsences.slice(0, 2).map(abs => {
                        const typeInfo = TYPE_ABSENCE.find(t => t.key === abs.type);
                        return (
                          <div key={abs.id} className="flex items-center gap-2 text-xs mb-1">
                            <span style={{ color: typeInfo?.color }}>{typeInfo?.label}</span>
                            <span className="text-[var(--text3)]">{fmtDate(abs.debut)} → {fmtDate(abs.fin)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Absences view */}
      {view === 'absences' && (
        <div className="grid grid-cols-3 gap-4">
          {/* Calendar */}
          <div className="col-span-2 card">
            <div className="card-header">
              <div className="flex items-center gap-4">
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={() => setCalendarMonth(prev => {
                    const newMonth = prev.month - 1;
                    return newMonth < 0 ? { year: prev.year - 1, month: 11 } : { ...prev, month: newMonth };
                  })}
                >←</button>
                <span className="font-condensed font-bold text-lg uppercase">
                  {MOIS_FR[calendarMonth.month]} {calendarMonth.year}
                </span>
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={() => setCalendarMonth(prev => {
                    const newMonth = prev.month + 1;
                    return newMonth > 11 ? { year: prev.year + 1, month: 0 } : { ...prev, month: newMonth };
                  })}
                >→</button>
              </div>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-7 gap-1">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                  <div key={day} className="text-center text-xs font-bold text-[var(--text3)] py-2">{day}</div>
                ))}
                {(() => {
                  const daysInMonth = getDaysInMonth(calendarMonth.year, calendarMonth.month);
                  let firstDay = getFirstDayOfMonth(calendarMonth.year, calendarMonth.month);
                  firstDay = firstDay === 0 ? 6 : firstDay - 1;
                  const cells = [];
                  
                  for (let i = 0; i < firstDay; i++) {
                    cells.push(<div key={`empty-${i}`} className="p-2" />);
                  }
                  
                  for (let day = 1; day <= daysInMonth; day++) {
                    const dateStr = `${calendarMonth.year}-${String(calendarMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const dayAbsences = absences.filter(a => dateStr >= a.debut && dateStr <= a.fin);
                    
                    cells.push(
                      <div 
                        key={day}
                        className={`p-1 min-h-[60px] rounded border ${dayAbsences.length > 0 ? 'border-[var(--accent)]' : 'border-[var(--border)]'}`}
                        style={{ background: dayAbsences.length > 0 ? 'var(--accent-dim)' : 'var(--bg3)' }}
                      >
                        <div className="text-xs font-bold mb-1">{day}</div>
                        {dayAbsences.map((abs, i) => {
                          const tech = getTech(abs.technicien_id);
                          const typeInfo = TYPE_ABSENCE.find(t => t.key === abs.type);
                          return (
                            <div key={i} className="text-xs truncate" style={{ color: tech?.couleur_hex }}>
                              {tech?.initiales}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  return cells;
                })()}
              </div>
            </div>
          </div>

          {/* Absences list */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Absences</span>
            </div>
            <div className="card-body space-y-2" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {absences.length === 0 ? (
                <p className="text-[var(--text3)] text-center py-4">Aucune absence planifiée</p>
              ) : (
                absences.map(abs => {
                  const tech = getTech(abs.technicien_id);
                  const typeInfo = TYPE_ABSENCE.find(t => t.key === abs.type);
                  const TypeIcon = typeInfo?.icon || Calendar;
                  return (
                    <div key={abs.id} className="p-3 rounded" style={{ background: 'var(--bg3)', borderLeft: `3px solid ${typeInfo?.color}` }}>
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <div className="tech-avatar" style={{ background: tech?.couleur_hex, width: 24, height: 24, fontSize: 9 }}>
                            {tech?.initiales}
                          </div>
                          <div>
                            <div className="font-medium text-sm">{tech?.prenom}</div>
                            <div className="flex items-center gap-1 text-xs" style={{ color: typeInfo?.color }}>
                              <TypeIcon size={12} /> {typeInfo?.label}
                            </div>
                          </div>
                        </div>
                        <button 
                          className="text-[var(--text3)] hover:text-[var(--red)]"
                          onClick={() => handleDeleteAbsence(abs.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="text-xs text-[var(--text3)] mt-2">
                        {fmtDate(abs.debut)} → {fmtDate(abs.fin)}
                      </div>
                      {abs.note && <div className="text-xs text-[var(--text3)] mt-1">{abs.note}</div>}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Soldes vacances view */}
      {view === 'soldes' && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-4">
              <span className="card-title">Soldes vacances {new Date().getFullYear()}</span>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => setShowAddEmployeeModal(true)}
              >
                <Plus size={14} /> Ajouter un employé
              </button>
            </div>
            <button 
              className="btn btn-ghost btn-sm text-[var(--text3)]"
              onClick={() => setIsUnlocked(false)}
            >
              <Lock size={14} /> Verrouiller
            </button>
          </div>
          <div className="card-body">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employé</th>
                  <th>Rôle</th>
                  <th>Taux</th>
                  <th>Jours total</th>
                  <th>Prorata</th>
                  <th>Pris</th>
                  <th>Restants</th>
                  <th>Maladie</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {techniciens.map(tech => {
                  const tauxActivite = soldesEdit[tech.id]?.taux_activite ?? tech.taux_activite ?? 100;
                  const total = soldesEdit[tech.id]?.jours_vacances_total ?? tech.jours_vacances_total ?? 25;
                  const prorata = Math.round(total * tauxActivite / 100 * 10) / 10;
                  const pris = soldesEdit[tech.id]?.jours_vacances_pris ?? tech.jours_vacances_pris ?? 0;
                  const restants = Math.round((prorata - pris) * 10) / 10;
                  const maladie = soldesEdit[tech.id]?.jours_maladie ?? tech.jours_maladie ?? 0;
                  const roleInfo = ROLE_OPTIONS.find(r => r.key === tech.role) || ROLE_OPTIONS.find(r => r.key === 'technicien');
                  
                  return (
                    <tr key={tech.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="tech-avatar" style={{ background: tech.couleur_hex }}>{tech.initiales}</div>
                          <div>
                            <div>{tech.prenom} {tech.nom}</div>
                            {tech.email && <div className="text-xs text-[var(--text3)]">{tech.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge" style={{ background: `${roleInfo.color}22`, color: roleInfo.color }}>
                          {roleInfo.label}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            className="input w-16"
                            min="10"
                            max="100"
                            step="10"
                            value={tauxActivite}
                            onChange={(e) => setSoldesEdit(prev => ({
                              ...prev,
                              [tech.id]: { ...prev[tech.id], taux_activite: parseInt(e.target.value) || 100 }
                            }))}
                          />
                          <span className="text-[var(--text3)]">%</span>
                        </div>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="input w-16"
                          value={total}
                          onChange={(e) => setSoldesEdit(prev => ({
                            ...prev,
                            [tech.id]: { ...prev[tech.id], jours_vacances_total: parseInt(e.target.value) || 0 }
                          }))}
                        />
                      </td>
                      <td>
                        <span className="font-mono text-[var(--text3)]">{prorata} j</span>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="input w-16"
                          value={pris}
                          onChange={(e) => setSoldesEdit(prev => ({
                            ...prev,
                            [tech.id]: { ...prev[tech.id], jours_vacances_pris: parseInt(e.target.value) || 0 }
                          }))}
                        />
                      </td>
                      <td>
                        <span className="font-mono font-bold" style={{ color: restants < 5 ? 'var(--red)' : 'var(--green)' }}>
                          {restants} j
                        </span>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="input w-16"
                          value={maladie}
                          onChange={(e) => setSoldesEdit(prev => ({
                            ...prev,
                            [tech.id]: { ...prev[tech.id], jours_maladie: parseInt(e.target.value) || 0 }
                          }))}
                        />
                      </td>
                      <td>
                        <div className="flex gap-2">
                          {soldesEdit[tech.id] && (
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => handleSaveSoldes(tech)}
                            >
                              <Check size={12} /> Sauvegarder
                            </button>
                          )}
                          <button 
                            className="btn btn-ghost btn-sm text-red-500"
                            onClick={() => setShowDeleteConfirm(tech)}
                            title="Supprimer cet employé"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal ajouter employé */}
      {showAddEmployeeModal && (
        <div className="panel-overlay" onClick={() => setShowAddEmployeeModal(false)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '500px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">Ajouter un employé</div>
              <button className="panel-close" onClick={() => setShowAddEmployeeModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Prénom *</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={newEmployee.prenom}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, prenom: e.target.value }))}
                    placeholder="Jean"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Nom *</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={newEmployee.nom}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, nom: e.target.value }))}
                    placeholder="Dupont"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Initiales *</label>
                  <input
                    type="text"
                    className="input w-full"
                    maxLength={3}
                    value={newEmployee.initiales}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, initiales: e.target.value.toUpperCase() }))}
                    placeholder="JD"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Couleur</label>
                  <input
                    type="color"
                    className="input w-full h-10"
                    value={newEmployee.couleur_hex}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, couleur_hex: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Email</label>
                <input
                  type="email"
                  className="input w-full"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="jean@no-name-motorcycles.com"
                />
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Rôle</label>
                <div className="grid grid-cols-2 gap-2">
                  {ROLE_OPTIONS.map(role => (
                    <button
                      key={role.key}
                      className={`btn ${newEmployee.role === role.key ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => setNewEmployee(prev => ({ 
                        ...prev, 
                        role: role.key,
                        vt_jour_max: role.key === 'technicien' ? 96 : 0
                      }))}
                      style={newEmployee.role === role.key ? { background: role.color } : {}}
                    >
                      {role.label}
                    </button>
                  ))}
                </div>
              </div>

              {newEmployee.role === 'technicien' && (
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">VT max/jour</label>
                  <input
                    type="number"
                    className="input w-full"
                    value={newEmployee.vt_jour_max}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, vt_jour_max: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              )}

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Taux d'activité</label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="10"
                    value={newEmployee.taux_activite}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, taux_activite: parseInt(e.target.value) }))}
                    className="flex-1"
                  />
                  <span className="font-mono font-bold w-12 text-right">{newEmployee.taux_activite}%</span>
                </div>
                <p className="text-xs text-[var(--text3)] mt-1">
                  Vacances: {Math.round(25 * newEmployee.taux_activite / 100 * 10) / 10} jours/an (prorata)
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button className="btn btn-ghost" onClick={() => setShowAddEmployeeModal(false)}>
                  Annuler
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleAddEmployee}
                  disabled={!newEmployee.prenom || !newEmployee.nom || !newEmployee.initiales}
                >
                  <Plus size={14} /> Ajouter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmer suppression */}
      {showDeleteConfirm && (
        <div className="panel-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '400px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title text-red-500">Supprimer un employé</div>
              <button className="panel-close" onClick={() => setShowDeleteConfirm(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 rounded" style={{ background: 'var(--red-dim)' }}>
                <p className="text-sm">
                  Êtes-vous sûr de vouloir supprimer <strong>{showDeleteConfirm.prenom} {showDeleteConfirm.nom}</strong> ?
                </p>
                <p className="text-xs text-[var(--text3)] mt-2">
                  Cette action supprimera également toutes ses absences. Cette action est irréversible.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button className="btn btn-ghost" onClick={() => setShowDeleteConfirm(null)}>
                  Annuler
                </button>
                <button 
                  className="btn"
                  style={{ background: 'var(--red)', color: 'white' }}
                  onClick={() => handleDeleteEmployee(showDeleteConfirm)}
                >
                  <Trash2 size={14} /> Supprimer définitivement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal nouvelle absence */}
      {showAbsenceModal && (
        <div className="panel-overlay" onClick={() => setShowAbsenceModal(false)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '450px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">Nouvelle absence</div>
              <button className="panel-close" onClick={() => setShowAbsenceModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Technicien</label>
                <select
                  className="input w-full"
                  value={absenceForm.technicien_id}
                  onChange={(e) => setAbsenceForm({ ...absenceForm, technicien_id: e.target.value })}
                >
                  <option value="">Sélectionner...</option>
                  {techniciens.map(t => (
                    <option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {TYPE_ABSENCE.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.key}
                        className={`p-3 rounded flex items-center gap-2 text-sm transition-all ${absenceForm.type === type.key ? 'ring-2' : ''}`}
                        style={{ 
                          background: absenceForm.type === type.key ? `${type.color}22` : 'var(--bg3)',
                          color: absenceForm.type === type.key ? type.color : 'var(--text2)'
                        }}
                        onClick={() => setAbsenceForm({ ...absenceForm, type: type.key })}
                      >
                        <Icon size={16} /> {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Du</label>
                  <input
                    type="date"
                    className="input w-full"
                    value={absenceForm.debut}
                    onChange={(e) => setAbsenceForm({ ...absenceForm, debut: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Au</label>
                  <input
                    type="date"
                    className="input w-full"
                    value={absenceForm.fin}
                    onChange={(e) => setAbsenceForm({ ...absenceForm, fin: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Note (optionnel)</label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="Raison, commentaire..."
                  value={absenceForm.note}
                  onChange={(e) => setAbsenceForm({ ...absenceForm, note: e.target.value })}
                />
              </div>
            </div>

            <button 
              className="btn btn-primary w-full mt-6"
              onClick={handleCreateAbsence}
              disabled={!absenceForm.technicien_id || !absenceForm.debut || !absenceForm.fin}
            >
              <Plus size={14} /> Créer l'absence
            </button>
          </div>
        </div>
      )}

      {/* Modal PIN admin */}
      {showPinModal && (
        <div className="panel-overlay" onClick={() => { setShowPinModal(null); setPinInput(''); setPinError(''); }}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '350px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">PIN Admin requis</div>
              <button className="panel-close" onClick={() => { setShowPinModal(null); setPinInput(''); setPinError(''); }}>
                <X size={20} />
              </button>
            </div>

            <div className="text-center mb-6">
              <Lock size={48} className="mx-auto text-[var(--accent)] mb-4" />
              <p className="text-sm text-[var(--text3)]">
                Entrez le PIN admin pour modifier les soldes de <strong>{showPinModal.prenom}</strong>
              </p>
            </div>

            <input
              type="password"
              className="input w-full text-center text-2xl tracking-widest"
              placeholder="••••"
              maxLength={4}
              value={pinInput}
              onChange={(e) => { setPinInput(e.target.value); setPinError(''); }}
              autoFocus
            />

            {pinError && (
              <p className="text-center text-sm mt-2" style={{ color: 'var(--red)' }}>{pinError}</p>
            )}

            <p className="text-center text-xs text-[var(--text3)] mt-2">PIN par défaut: 1234</p>

            <button 
              className="btn btn-primary w-full mt-4"
              onClick={handleVerifyPin}
              disabled={pinInput.length < 4}
            >
              <Check size={14} /> Valider
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Rapport Hebdomadaire Component
const RapportHebdoView = () => {
  const [semainesAvant, setSemainesAvant] = useState(1); // Semaine dernière par défaut
  const [rapport, setRapport] = useState(null);
  const [htmlPreview, setHtmlPreview] = useState('');
  const [historique, setHistorique] = useState([]);
  const [tendances, setTendances] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [destinataires, setDestinataires] = useState('admin@nnm.ch');

  useEffect(() => {
    loadRapport();
    loadHistorique();
    loadConfig();
    loadTendances();
  }, [semainesAvant]);

  const loadRapport = async () => {
    setLoading(true);
    try {
      const res = await rapportsAPI.getPreview(semainesAvant);
      setRapport(res.data.rapport);
      setHtmlPreview(res.data.html);
    } catch (err) {
      console.error('Error loading rapport:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTendances = async () => {
    try {
      const res = await rapportsAPI.getTendances(6);
      setTendances(res.data);
    } catch (err) {
      console.error('Error loading tendances:', err);
    }
  };

  const loadHistorique = async () => {
    try {
      const res = await rapportsAPI.getHistorique();
      setHistorique(res.data);
    } catch (err) {
      console.error('Error loading historique:', err);
    }
  };

  const loadConfig = async () => {
    try {
      const res = await rapportsAPI.getConfig();
      setConfig(res.data);
      if (res.data.destinataires?.length > 0) {
        setDestinataires(res.data.destinataires.join(', '));
      }
    } catch (err) {
      console.error('Error loading config:', err);
    }
  };

  const handleEnvoyer = async () => {
    setSending(true);
    try {
      const destList = destinataires.split(',').map(d => d.trim()).filter(d => d);
      await rapportsAPI.envoyer(semainesAvant, destList);
      await loadHistorique();
      alert('Rapport envoyé avec succès (MOCK)');
    } catch (err) {
      console.error('Error sending rapport:', err);
      alert('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      const destList = destinataires.split(',').map(d => d.trim()).filter(d => d);
      await rapportsAPI.updateConfig({
        destinataires: destList,
        actif: config?.actif ?? true
      });
      setShowConfig(false);
      alert('Configuration sauvegardée');
    } catch (err) {
      console.error('Error saving config:', err);
    }
  };

  const getSemaineName = (offset) => {
    const now = new Date();
    const daysSinceMonday = now.getDay() === 0 ? 6 : now.getDay() - 1;
    const monday = new Date(now);
    monday.setDate(now.getDate() - daysSinceMonday - (7 * offset));
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    return `${monday.toLocaleDateString('fr-CH')} - ${friday.toLocaleDateString('fr-CH')}`;
  };

  if (loading && !rapport) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[var(--accent)]" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="rapport-hebdo-view">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">Rapport Hebdomadaire KPI</h3>
          <div className="flex items-center gap-2">
            <button 
              className="btn btn-ghost btn-sm"
              onClick={() => setSemainesAvant(prev => prev + 1)}
              title="Semaine précédente"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm px-3 py-1 rounded" style={{ background: 'var(--bg3)' }}>
              {semainesAvant === 0 ? 'Semaine en cours' : semainesAvant === 1 ? 'Semaine dernière' : `Il y a ${semainesAvant} semaines`}
            </span>
            <button 
              className="btn btn-ghost btn-sm"
              onClick={() => setSemainesAvant(prev => Math.max(0, prev - 1))}
              disabled={semainesAvant === 0}
              title="Semaine suivante"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            className="btn btn-ghost"
            onClick={() => setShowConfig(!showConfig)}
          >
            <Settings size={16} /> Config
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleEnvoyer}
            disabled={sending}
          >
            {sending ? <RefreshCw className="animate-spin" size={16} /> : <Send size={16} />}
            Envoyer par email
          </button>
        </div>
      </div>

      {/* Config Panel */}
      {showConfig && (
        <div className="card mb-4">
          <div className="card-header">
            <span className="card-title">Configuration du rapport automatique</span>
            {config?.sendgrid_configured ? (
              <span className="badge badge-green">SendGrid actif</span>
            ) : (
              <span className="badge badge-yellow">Mode MOCK (SendGrid non configuré)</span>
            )}
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="block text-sm text-[var(--text3)] mb-1">Destinataires (séparés par virgule)</label>
              <input 
                type="text"
                className="input w-full"
                value={destinataires}
                onChange={(e) => setDestinataires(e.target.value)}
                placeholder="admin@nnm.ch, manager@nnm.ch"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox"
                  checked={config?.actif ?? true}
                  onChange={(e) => setConfig(prev => ({ ...prev, actif: e.target.checked }))}
                />
                <span className="text-sm">Envoi automatique chaque mardi à 7h</span>
              </label>
            </div>
            {!config?.sendgrid_configured && (
              <div className="p-3 rounded text-sm" style={{ background: 'var(--yellow-dim)', color: 'var(--yellow)' }}>
                ⚠️ Pour activer l'envoi email réel, ajoutez <code>SENDGRID_API_KEY</code> et <code>SENDER_EMAIL</code> dans le fichier backend/.env
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button className="btn btn-ghost" onClick={() => setShowConfig(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSaveConfig}>Sauvegarder</button>
            </div>
          </div>
        </div>
      )}

      {/* KPIs Summary */}
      {rapport && (
        <div className="stats-grid mb-6">
          <div className="stat-card">
            <div className="stat-label">VT Réalisés</div>
            <div className="stat-value" style={{ color: 'var(--accent)' }}>{rapport.vt_realises?.toFixed(1) || 0}</div>
            <div className="stat-sub">sur {rapport.vt_vendus?.toFixed(1) || 0} vendus</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Rendement</div>
            <div className="stat-value" style={{ color: rapport.rendement_pct >= 100 ? 'var(--green)' : rapport.rendement_pct >= 85 ? 'var(--yellow)' : 'var(--red)' }}>
              {rapport.rendement_pct || 0}%
            </div>
            <div className="stat-sub">VT vendus / réalisés</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">CA Main d'œuvre</div>
            <div className="stat-value">{fmtCHF(rapport.ca_main_oeuvre || 0)}</div>
            <div className="stat-sub">125 CHF/VT</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Taux Occupation</div>
            <div className="stat-value" style={{ color: rapport.taux_occupation >= 80 ? 'var(--green)' : rapport.taux_occupation >= 50 ? 'var(--yellow)' : 'var(--text3)' }}>
              {rapport.taux_occupation || 0}%
            </div>
            <div className="stat-sub">{rapport.ots_termines || 0} OT terminés</div>
          </div>
        </div>
      )}

      {/* Graphique de Tendance */}
      {tendances.length > 0 && (
        <div className="card mb-6">
          <div className="card-header">
            <span className="card-title">Évolution sur 6 semaines</span>
            <span className="text-sm text-[var(--text3)]">Rendement & VT Réalisés</span>
          </div>
          <div className="card-body" style={{ height: '280px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={tendances} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis 
                  dataKey="semaine" 
                  stroke="var(--text3)" 
                  tick={{ fill: 'var(--text3)', fontSize: 11 }}
                  tickFormatter={(val) => val.split('-')[1]}
                />
                <YAxis 
                  yAxisId="left"
                  stroke="var(--text3)" 
                  tick={{ fill: 'var(--text3)', fontSize: 11 }}
                  domain={[0, 'auto']}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right"
                  stroke="var(--text3)" 
                  tick={{ fill: 'var(--text3)', fontSize: 11 }}
                  domain={[0, 150]}
                  tickFormatter={(val) => `${val}%`}
                />
                <Tooltip 
                  contentStyle={{ 
                    background: 'var(--bg2)', 
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    color: 'var(--text1)'
                  }}
                  formatter={(value, name) => {
                    if (name === 'Rendement') return [`${value}%`, name];
                    if (name === 'CA') return [fmtCHF(value), name];
                    return [value, name];
                  }}
                />
                <Legend 
                  wrapperStyle={{ color: 'var(--text2)' }}
                />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="vt_realises" 
                  name="VT Réalisés"
                  stroke="#e8621a" 
                  strokeWidth={2}
                  dot={{ fill: '#e8621a', strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="rendement_pct" 
                  name="Rendement"
                  stroke="#4caf75" 
                  strokeWidth={2}
                  dot={{ fill: '#4caf75', strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="ca_main_oeuvre" 
                  name="CA"
                  stroke="#5b9cf6" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#5b9cf6', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        {/* Performance par technicien */}
        <div className="col-span-2">
          <div className="card">
            <div className="card-header">
              <span className="card-title">Performance par technicien</span>
              <span className="text-sm text-[var(--text3)]">{rapport?.semaine}</span>
            </div>
            <div className="card-body p-0">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Technicien</th>
                    <th className="text-right">VT Réalisés</th>
                    <th className="text-right">Rendement</th>
                    <th className="text-right">CA</th>
                  </tr>
                </thead>
                <tbody>
                  {rapport?.par_technicien?.map(tech => (
                    <tr key={tech.id}>
                      <td className="font-medium">{tech.nom}</td>
                      <td className="text-right">{tech.vt_realises?.toFixed(1)}</td>
                      <td className="text-right">
                        <span style={{ color: tech.rendement_pct >= 100 ? 'var(--green)' : tech.rendement_pct >= 85 ? 'var(--yellow)' : 'var(--red)' }}>
                          {tech.rendement_pct}%
                        </span>
                      </td>
                      <td className="text-right">{fmtCHF(tech.ca)}</td>
                    </tr>
                  ))}
                  {(!rapport?.par_technicien || rapport.par_technicien.length === 0) && (
                    <tr>
                      <td colSpan={4} className="text-center text-[var(--text3)] py-8">
                        Aucune donnée pour cette période
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Prévisualisation Email */}
          <div className="card mt-4">
            <div className="card-header">
              <span className="card-title">Prévisualisation de l'email</span>
            </div>
            <div className="card-body p-0">
              <div 
                className="w-full rounded-b-lg overflow-hidden"
                style={{ maxHeight: '500px', overflowY: 'auto' }}
                dangerouslySetInnerHTML={{ __html: htmlPreview }}
              />
            </div>
          </div>
        </div>

        {/* Historique */}
        <div className="col-span-1">
          <div className="card">
            <div className="card-header">
              <span className="card-title">Historique des envois</span>
            </div>
            <div className="card-body space-y-3">
              {historique.length === 0 ? (
                <p className="text-sm text-[var(--text3)] text-center py-4">Aucun rapport envoyé</p>
              ) : (
                historique.slice(0, 10).map(h => (
                  <div key={h.id} className="p-3 rounded" style={{ background: 'var(--bg3)' }}>
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm">{h.semaine}</span>
                      <span className={`badge ${h.statut === 'envoye' ? 'badge-green' : 'badge-yellow'}`}>
                        {h.statut === 'envoye' ? 'Envoyé' : 'Mock'}
                      </span>
                    </div>
                    <div className="text-xs text-[var(--text3)]">
                      {new Date(h.created_at).toLocaleDateString('fr-CH')} à {new Date(h.created_at).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className="text-xs text-[var(--text3)] mt-1">
                      → {h.destinataires?.join(', ')}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Automatismes Component
const Automatismes = ({ clients, motos, leads, ots }) => {
  const [view, setView] = useState('file'); // file, templates, config
  const [filter, setFilter] = useState('tous');
  const [automatismes, setAutomatismes] = useState([]);
  const [stats, setStats] = useState(null);
  const [regles, setRegles] = useState([]);
  const [selectedAuto, setSelectedAuto] = useState(null);
  const [loading, setLoading] = useState(true);

  const getClient = (id) => clients.find(c => c.id === id);
  const getMoto = (id) => motos.find(m => m.id === id);
  const getLead = (id) => leads.find(l => l.id === id);
  const getOT = (id) => ots.find(o => o.id === id);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [autoRes, statsRes, reglesRes] = await Promise.all([
          automatismesAPI.getAll(filter === 'tous' ? null : filter),
          automatismesAPI.getStats(),
          automatismesAPI.getRegles()
        ]);
        setAutomatismes(autoRes.data);
        setStats(statsRes.data);
        setRegles(reglesRes.data);
      } catch (err) {
        console.error('Error loading automatismes:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filter]);

  const handleEnvoyer = async (autoId) => {
    try {
      await automatismesAPI.envoyer(autoId);
      setAutomatismes(prev => prev.map(a => 
        a.id === autoId ? { ...a, statut: 'envoye', date_envoi: new Date().toISOString().split('T')[0] } : a
      ));
      setStats(prev => ({ ...prev, a_envoyer: prev.a_envoyer - 1, envoyes_mois: prev.envoyes_mois + 1 }));
    } catch (err) {
      console.error('Error sending:', err);
    }
  };

  const handleIgnorer = async (autoId) => {
    try {
      await automatismesAPI.ignorer(autoId);
      setAutomatismes(prev => prev.map(a => 
        a.id === autoId ? { ...a, statut: 'ignore' } : a
      ));
      setStats(prev => ({ ...prev, a_envoyer: prev.a_envoyer - 1, ignores: prev.ignores + 1 }));
    } catch (err) {
      console.error('Error ignoring:', err);
    }
  };

  const handleRelancer = async (autoId) => {
    try {
      await automatismesAPI.relancer(autoId);
      setAutomatismes(prev => prev.map(a => 
        a.id === autoId ? { ...a, statut: 'a_envoyer', date_envoi: null } : a
      ));
    } catch (err) {
      console.error('Error relaunching:', err);
    }
  };

  const toggleRegle = async (regleId, actif) => {
    try {
      await automatismesAPI.updateRegle(regleId, { actif: !actif });
      setRegles(prev => prev.map(r => r.id === regleId ? { ...r, actif: !actif } : r));
    } catch (err) {
      console.error('Error toggling rule:', err);
    }
  };

  const TYPE_LABELS = {
    relance_offre: { label: 'Relance offre de vente', color: 'var(--accent)' },
    relance_ot: { label: 'Relance OT non confirmé', color: 'var(--yellow)' },
    satisfaction: { label: 'Satisfaction post-atelier', color: 'var(--green)' },
    relance_client: { label: 'Relance client inactif', color: 'var(--blue)' },
  };

  const STATUT_LABELS = {
    a_envoyer: { label: 'À ENVOYER', cls: 'badge-yellow' },
    envoye: { label: 'ENVOYÉ', cls: 'badge-green' },
    ignore: { label: 'IGNORÉ', cls: 'badge-gray' },
  };

  const daysSince = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    return Math.floor((now - d) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[var(--accent)]" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="automatismes-view">
      {/* KPIs */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">En attente d'envoi</div>
          <div className="stat-value" style={{ color: stats?.a_envoyer > 0 ? 'var(--yellow)' : 'var(--green)' }}>
            {stats?.a_envoyer || 0}
          </div>
          <div className="stat-sub">emails à traiter</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Envoyés ce mois</div>
          <div className="stat-value" style={{ color: 'var(--green)' }}>{stats?.envoyes_mois || 0}</div>
          <div className="stat-sub">mars 2025</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Ignorés</div>
          <div className="stat-value" style={{ color: 'var(--text3)' }}>{stats?.ignores || 0}</div>
          <div className="stat-sub">non envoyés manuellement</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Règles actives</div>
          <div className="stat-value">{stats?.regles_actives || 0}</div>
          <div className="stat-sub">automatismes configurés</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex justify-between items-center mt-6 mb-4">
        <div className="filter-tabs">
          <button className={`filter-tab ${view === 'file' ? 'active' : ''}`} onClick={() => setView('file')}>
            <Mail size={14} /> File d'envoi
          </button>
          <button className={`filter-tab ${view === 'rapport' ? 'active' : ''}`} onClick={() => setView('rapport')}>
            <FileBarChart size={14} /> Rapport Hebdo
          </button>
          <button className={`filter-tab ${view === 'templates' ? 'active' : ''}`} onClick={() => setView('templates')}>
            <FileText size={14} /> Templates
          </button>
          <button className={`filter-tab ${view === 'config' ? 'active' : ''}`} onClick={() => setView('config')}>
            <Settings size={14} /> Configuration
          </button>
        </div>
      </div>

      {view === 'file' && (
        <>
          {/* Filters */}
          <div className="filter-tabs mb-4">
            {['tous', 'a_envoyer', 'envoye', 'ignore'].map(f => (
              <button
                key={f}
                className={`filter-tab ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'tous' ? 'Tous' : STATUT_LABELS[f]?.label}
              </button>
            ))}
          </div>

          {/* List + Preview */}
          <div className="grid grid-cols-5 gap-4">
            {/* List */}
            <div className="col-span-3 space-y-3">
              {automatismes.length === 0 ? (
                <div className="card">
                  <div className="card-body text-center py-12">
                    <Check size={48} className="mx-auto text-[var(--green)] mb-4" />
                    <p className="text-[var(--text3)]">Aucun automatisme en file d'attente</p>
                  </div>
                </div>
              ) : (
                automatismes.map(auto => {
                  const client = getClient(auto.client_id);
                  const moto = getMoto(auto.moto_id);
                  const lead = auto.lead_id ? getLead(auto.lead_id) : null;
                  const ot = auto.ot_id ? getOT(auto.ot_id) : null;
                  const typeInfo = TYPE_LABELS[auto.type];
                  const statutInfo = STATUT_LABELS[auto.statut];
                  const days = daysSince(auto.date_declenchement);

                  return (
                    <div 
                      key={auto.id} 
                      className={`card cursor-pointer transition-all ${selectedAuto?.id === auto.id ? 'ring-2 ring-[var(--accent)]' : ''}`}
                      onClick={() => setSelectedAuto(auto)}
                      style={{ borderLeft: `4px solid ${typeInfo?.color || 'var(--border)'}` }}
                    >
                      <div className="card-body py-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="text-xs font-medium" style={{ color: typeInfo?.color }}>
                              {typeInfo?.label}
                            </span>
                            <span className={`badge ${statutInfo?.cls} ml-2`}>{statutInfo?.label}</span>
                          </div>
                          <div className="flex gap-1">
                            {auto.statut === 'a_envoyer' && (
                              <>
                                <button 
                                  className="btn btn-primary btn-sm"
                                  onClick={(e) => { e.stopPropagation(); handleEnvoyer(auto.id); }}
                                  title="Envoyer"
                                >
                                  <Send size={12} /> Envoyer
                                </button>
                                <button 
                                  className="btn btn-ghost btn-sm"
                                  onClick={(e) => { e.stopPropagation(); handleIgnorer(auto.id); }}
                                  title="Ignorer"
                                >
                                  <X size={14} />
                                </button>
                              </>
                            )}
                            {(auto.statut === 'envoye' || auto.statut === 'ignore') && (
                              <button 
                                className="btn btn-ghost btn-sm"
                                onClick={(e) => { e.stopPropagation(); handleRelancer(auto.id); }}
                                title="Relancer"
                              >
                                <RotateCcw size={14} /> Relancer
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="font-medium">{client?.prenom} {client?.nom}</div>
                        <div className="text-sm text-[var(--text2)]">
                          {lead?.moto_libelle || (moto ? `${moto.marque} ${moto.modele}` : ot?.description || '—')}
                        </div>

                        <div className="flex items-center gap-4 mt-2 text-xs text-[var(--text3)]">
                          <span>Déclenché il y a {days}j</span>
                          <span>Prévu {fmtDate(auto.date_prevue)}</span>
                          {auto.date_envoi && <span style={{ color: 'var(--green)' }}>Envoyé {fmtDate(auto.date_envoi)}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Preview */}
            <div className="col-span-2">
              {selectedAuto ? (
                <div className="card sticky top-4">
                  <div className="card-header">
                    <span className="card-title">Prévisualisation email</span>
                    <button className="panel-close" onClick={() => setSelectedAuto(null)}>
                      <X size={16} />
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="mb-4">
                      <div className="text-xs text-[var(--text3)] mb-1">À</div>
                      <div className="text-sm">{getClient(selectedAuto.client_id)?.email || 'email@client.ch'}</div>
                    </div>
                    <div className="mb-4">
                      <div className="text-xs text-[var(--text3)] mb-1">Sujet</div>
                      <div className="text-sm font-medium">{selectedAuto.sujet}</div>
                    </div>
                    <div className="p-4 rounded text-sm leading-relaxed whitespace-pre-line" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                      {selectedAuto.corps}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="card-body text-center py-12">
                    <Eye size={32} className="mx-auto text-[var(--text3)] mb-3" />
                    <p className="text-[var(--text3)]">Sélectionnez un automatisme pour prévisualiser l'email</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {view === 'rapport' && (
        <RapportHebdoView />
      )}

      {view === 'templates' && (
        <div className="space-y-4">
          {regles.map(regle => (
            <div key={regle.id} className="card">
              <div className="card-header">
                <div>
                  <span className="card-title">{regle.nom}</span>
                  <span className={`badge ml-2 ${regle.actif ? 'badge-green' : 'badge-gray'}`}>
                    {regle.actif ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <span className="text-sm text-[var(--text3)]">Délai: {regle.delai_jours} jours</span>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <div className="text-xs text-[var(--text3)] mb-1">Sujet</div>
                  <div className="p-2 rounded text-sm" style={{ background: 'var(--bg3)' }}>{regle.template_sujet}</div>
                </div>
                <div>
                  <div className="text-xs text-[var(--text3)] mb-1">Corps</div>
                  <div className="p-3 rounded text-sm whitespace-pre-line" style={{ background: 'var(--bg3)' }}>
                    {regle.template_corps}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'config' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Règles d'automatismes</span>
          </div>
          <div className="card-body">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Règle</th>
                  <th>Type</th>
                  <th>Délai</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {regles.map(regle => (
                  <tr key={regle.id}>
                    <td className="font-medium">{regle.nom}</td>
                    <td>
                      <span className="text-sm" style={{ color: TYPE_LABELS[regle.type]?.color }}>
                        {TYPE_LABELS[regle.type]?.label}
                      </span>
                    </td>
                    <td>{regle.delai_jours} jours</td>
                    <td>
                      <span className={`badge ${regle.actif ? 'badge-green' : 'badge-gray'}`}>
                        {regle.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      <button 
                        className="btn btn-ghost btn-sm"
                        onClick={() => toggleRegle(regle.id, regle.actif)}
                      >
                        {regle.actif ? 'Désactiver' : 'Activer'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Dashboard Atelier Component
const DashboardAtelier = ({ ots, techniciens, clients, motos, slots }) => {
  const [rappelsSent, setRappelsSent] = useState({});

  const getClient = (id) => clients.find(c => c.id === id);
  const getMoto = (id) => motos.find(m => m.id === id);
  const getTech = (id) => techniciens.find(t => t.id === id);
  const getOT = (id) => ots.find(o => o.id === id);

  // Calculate stats
  const otsTermines = ots.filter(o => o.statut === 'termine');
  const caMainOeuvre = otsTermines.reduce((s, o) => s + (o.vt_realises || 0) * 15, 0); // 15 CHF/VT approx
  
  // Rendement atelier (VT vendus / VT réalisés)
  const totalVtVendus = otsTermines.reduce((s, o) => s + (o.vt_vendus || 0), 0);
  const totalVtRealises = otsTermines.reduce((s, o) => s + (o.vt_realises || 0), 0);
  const rendementAtelier = totalVtRealises > 0 ? Math.round((totalVtVendus / totalVtRealises) * 100) : 0;

  // Alertes révision (motos avec révision dépassée ou proche)
  const alertesRevision = motos.filter(m => {
    if (!m.prochain_service_km && !m.prochain_service_date) return false;
    if (m.prochain_service_km && m.km_actuel >= m.prochain_service_km) return true;
    if (m.prochain_service_date && new Date(m.prochain_service_date) <= new Date()) return true;
    return false;
  });

  // RDV demain (slots pour demain)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];
  // For demo, use slots from March 18-19, 2025
  const rdvDemain = slots.filter(s => 
    (s.date === '2025-03-19' || s.date === '2025-03-18') && 
    s.statut !== 'annule'
  ).slice(0, 3);

  // VT stats par mois (mock data for chart)
  const vtParMois = [
    { mois: 'Jan', vendus: 480, realises: 520, ca: 8600 },
    { mois: 'Fév', vendus: 520, realises: 495, ca: 10400 },
    { mois: 'Mar', vendus: 380, realises: 410, ca: 7400 },
  ];

  // Rendement par technicien
  const rendementParTech = techniciens.map(tech => {
    const techOTs = otsTermines.filter(o => o.technicien_id === tech.id);
    const vtV = techOTs.reduce((s, o) => s + (o.vt_vendus || 0), 0);
    const vtR = techOTs.reduce((s, o) => s + (o.vt_realises || 0), 0);
    const rend = vtR > 0 ? Math.round((vtV / vtR) * 100) : 100;
    return { ...tech, vtVendus: vtV, vtRealises: vtR, rendement: rend };
  });

  const handleSendRappel = (slotId) => {
    setRappelsSent(prev => ({ ...prev, [slotId]: true }));
    // In real app, would call API to send reminder
  };

  const maxCa = Math.max(...vtParMois.map(v => v.ca));

  return (
    <div data-testid="dashboard-atelier-view">
      {/* KPIs Row */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">CA Main d'œuvre</div>
          <div className="stat-value" style={{ color: 'var(--accent)' }}>CHF {Math.round(caMainOeuvre / 1000)}k</div>
          <div className="stat-sub">3 derniers mois · {totalVtRealises} VT</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Rendement Atelier</div>
          <div className="stat-value" style={{ color: rendementAtelier >= 100 ? 'var(--green)' : 'var(--red)' }}>
            {rendementAtelier}%
          </div>
          <div className="stat-sub">VT vendus / réalisés</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Alertes Révision</div>
          <div className="stat-value" style={{ color: alertesRevision.length > 0 ? 'var(--red)' : 'var(--green)' }}>
            {alertesRevision.length}
          </div>
          <div className="stat-sub">{alertesRevision.length > 0 ? `${alertesRevision.length} urgentes` : '0 urgentes'} · 0 à surveiller</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Rappels RDV demain</div>
          <div className="stat-value">{rdvDemain.length}</div>
          <div className="stat-sub">emails à envoyer</div>
        </div>
      </div>

      <div className="grid-2 mt-4">
        {/* VT Vendus vs Réalisés Chart */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">VT Vendus vs Réalisés — Rentabilité</span>
            <div className="flex gap-2">
              <button className="btn btn-ghost btn-sm active">3 mois</button>
              <button className="btn btn-ghost btn-sm">Tout</button>
            </div>
          </div>
          <div className="card-body">
            {/* Legend */}
            <div className="flex gap-4 mb-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ background: 'var(--accent)' }} />
                <span className="text-[var(--text3)]">VT vendus/facturés</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ background: 'var(--green)' }} />
                <span className="text-[var(--text3)]">VT réalisés</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ background: 'var(--text3)' }} />
                <span className="text-[var(--text3)]">Rendement %</span>
              </div>
            </div>

            {/* Chart */}
            <div className="flex items-end gap-4 h-48">
              {vtParMois.map((mois, idx) => {
                const maxVt = Math.max(...vtParMois.map(m => Math.max(m.vendus, m.realises)));
                const hVendus = (mois.vendus / maxVt) * 160;
                const hRealises = (mois.realises / maxVt) * 160;
                const rend = Math.round((mois.vendus / mois.realises) * 100);
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div className="flex items-end gap-1 h-40">
                      <div 
                        className="w-8 rounded-t" 
                        style={{ height: hVendus, background: 'var(--accent)' }}
                        title={`Vendus: ${mois.vendus} VT`}
                      />
                      <div 
                        className="w-8 rounded-t" 
                        style={{ height: hRealises, background: 'var(--green)' }}
                        title={`Réalisés: ${mois.realises} VT`}
                      />
                    </div>
                    <div className="text-xs text-[var(--text3)] mt-2">{mois.mois}</div>
                    <div className="text-xs" style={{ color: rend >= 100 ? 'var(--green)' : 'var(--yellow)' }}>
                      ↗ {rend}%
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CA by month */}
            <div className="flex gap-4 mt-4 pt-4 border-t border-[var(--border)]">
              {vtParMois.map((mois, idx) => (
                <div key={idx} className="flex-1 text-center">
                  <div className="font-condensed font-bold text-lg" style={{ color: 'var(--accent)' }}>
                    CHF {(mois.ca / 1000).toFixed(1)}k
                  </div>
                  <div className="text-xs text-[var(--text3)]">{mois.mois}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Rendement par Technicien */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Rendement par Technicien</span>
          </div>
          <div className="card-body space-y-4">
            {rendementParTech.map(tech => {
              const barColor = tech.rendement >= 100 ? 'var(--green)' : tech.rendement >= 90 ? 'var(--yellow)' : 'var(--red)';
              return (
                <div key={tech.id}>
                  <div className="flex items-center gap-3 mb-2">
                    <div 
                      className="tech-avatar" 
                      style={{ background: tech.couleur_hex }}
                    >
                      {tech.initiales}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{tech.prenom}</span>
                        <span className="font-condensed text-2xl font-bold" style={{ color: barColor }}>
                          {tech.rendement}%
                        </span>
                      </div>
                      <div className="text-xs text-[var(--text3)]">
                        {tech.vtVendus > 0 ? `${tech.vtVendus} VT · ${tech.vtRealises} VT réalisés` : 'rendement'}
                      </div>
                    </div>
                  </div>
                  <div className="charge-bar">
                    <div 
                      className="charge-fill" 
                      style={{ 
                        width: `${Math.min(tech.rendement, 120)}%`, 
                        background: barColor,
                        maxWidth: '100%'
                      }} 
                    />
                  </div>
                </div>
              );
            })}

            {/* Alert if rendement > 100 */}
            {rendementParTech.some(t => t.rendement > 100) && (
              <div className="mt-4 p-3 rounded" style={{ background: 'var(--red-dim)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-[var(--red)] mt-0.5" />
                  <div className="text-sm">
                    <span style={{ color: 'var(--red)' }}>⚠ Rendement 100%</span>
                    <span className="text-[var(--text3)]"> — les techniciens réalisent plus de VT que vendus. Revoir le catalogue de prestations.</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid-2 mt-4">
        {/* Alertes Révision */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Alertes Révision</span>
            <span className="badge badge-red">{alertesRevision.length} urgentes</span>
          </div>
          <div className="card-body">
            {alertesRevision.length === 0 ? (
              <div className="text-center py-8">
                <Check size={32} className="mx-auto text-[var(--green)] mb-2" />
                <p className="text-[var(--text3)]">✓ Toutes les motos sont à jour</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alertesRevision.map(moto => {
                  const client = getClient(moto.client_id);
                  return (
                    <div key={moto.id} className="p-3 rounded" style={{ background: 'var(--bg3)', border: '1px solid var(--border2)' }}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{moto.marque} {moto.modele}</div>
                          <div className="text-xs text-[var(--text3)]">{client?.prenom} {client?.nom}</div>
                        </div>
                        <span className="badge badge-red">Urgent</span>
                      </div>
                      <div className="text-xs text-[var(--text3)] mt-2">
                        {moto.km_actuel?.toLocaleString()} km · Prochain service: {moto.prochain_service_km?.toLocaleString()} km
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Rappels RDV Demain */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Rappels RDV — Demain 19 Mars</span>
            <button className="btn btn-primary btn-sm">
              <Send size={12} /> {rdvDemain.length} à envoyer
            </button>
          </div>
          <div className="card-body">
            {rdvDemain.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[var(--text3)]">Aucun RDV demain</p>
              </div>
            ) : (
              <div className="space-y-4">
                {rdvDemain.map(slot => {
                  const ot = getOT(slot.ordre_travail_id);
                  const client = ot ? getClient(ot.client_id) : null;
                  const moto = ot ? getMoto(ot.moto_id) : null;
                  const tech = getTech(slot.technicien_id);
                  const isSent = rappelsSent[slot.id];

                  return (
                    <div key={slot.id} className="p-4 rounded" style={{ background: 'var(--bg3)', border: '1px solid var(--border2)' }}>
                      <div className="flex gap-4">
                        <div className="text-center">
                          <div className="font-condensed text-2xl font-bold" style={{ color: 'var(--accent)' }}>
                            {slot.heure_debut}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-bold">{client?.prenom} {client?.nom}</div>
                              <div className="text-sm text-[var(--text2)]">
                                {moto?.marque} {moto?.modele} · {ot?.description}
                              </div>
                              <div className="text-xs text-[var(--text3)] mt-1">
                                {slot.duree_minutes} min · {tech?.prenom}
                              </div>
                            </div>
                            <button 
                              className={`btn btn-sm ${isSent ? 'btn-ghost' : 'btn-primary'}`}
                              onClick={() => handleSendRappel(slot.id)}
                              disabled={isSent}
                            >
                              {isSent ? '✓ Envoyé' : '✉ Envoyer rappel'}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Email preview */}
                      <div className="mt-3 p-3 rounded text-xs" style={{ background: 'var(--bg)', border: '1px dashed var(--border2)' }}>
                        <div className="text-[var(--text3)]">
                          Sujet : "Rappel RDV demain — votre {moto?.marque} {moto?.modele} chez No Name Motorcycles"
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Template */}
            <div className="mt-4 pt-4 border-t border-[var(--border)]">
              <div className="text-xs font-bold text-[var(--text3)] uppercase tracking-wider mb-2">Template Rappel RDV</div>
              <div className="text-xs text-[var(--text3)] leading-relaxed">
                Bonjour <span style={{ color: 'var(--accent)' }}>[[prenom]]</span>,<br /><br />
                Nous vous rappelons votre RDV demain <span style={{ color: 'var(--accent)' }}>[[date]]</span> à <span style={{ color: 'var(--accent)' }}>[[heure]]</span> chez No Name Motorcycles pour votre <span style={{ color: 'var(--accent)' }}>[[moto]]</span>.<br /><br />
                En cas d'empêchement, contactez-nous au +41 32 XXX XX XX.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Parc Motos Component
const ParcMotos = ({ motos, clients, ots, onUpdate }) => {
  const [view, setView] = useState('liste'); // liste, alertes, calendrier
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selectedMoto, setSelectedMoto] = useState(null);
  const [motoHistorique, setMotoHistorique] = useState(null);
  const [alertes, setAlertes] = useState([]);
  const [rappels, setRappels] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAppelModal, setShowAppelModal] = useState(null);
  const [csvData, setCsvData] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [appelForm, setAppelForm] = useState({ resultat: '', notes: '', rappel_date: '', rappel_heure: '' });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [calendarRappels, setCalendarRappels] = useState([]);

  const getClient = (id) => clients.find(c => c.id === id);

  // Load alertes
  useEffect(() => {
    const loadAlertes = async () => {
      try {
        const res = await motosAPI.getAlertesRevision();
        setAlertes(res.data);
      } catch (err) {
        console.error('Error loading alertes:', err);
      }
    };
    loadAlertes();
  }, [motos]);

  // Load rappels for calendar
  useEffect(() => {
    const loadCalendarRappels = async () => {
      try {
        const res = await tentativesAppelAPI.getCalendrier(calendarMonth.year, calendarMonth.month + 1);
        setCalendarRappels(res.data);
      } catch (err) {
        console.error('Error loading calendar rappels:', err);
      }
    };
    if (view === 'calendrier') {
      loadCalendarRappels();
    }
  }, [view, calendarMonth]);

  // Load moto historique
  const loadHistorique = async (motoId) => {
    try {
      const res = await motosAPI.getHistorique(motoId);
      setMotoHistorique(res.data);
    } catch (err) {
      console.error('Error loading historique:', err);
    }
  };

  // Search
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    try {
      const res = await motosAPI.search(searchQuery);
      setSearchResults(res.data);
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  // Import CSV
  const handleImportCSV = async () => {
    try {
      const lines = csvData.trim().split('\n');
      const headers = lines[0].split(';').map(h => h.trim().toLowerCase());
      const motosData = lines.slice(1).map(line => {
        const values = line.split(';');
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = values[i]?.trim();
        });
        return {
          vin: obj.vin,
          marque: obj.marque || 'BMW',
          modele: obj.modele,
          annee: obj.annee,
          immatriculation: obj.immatriculation,
          km_actuel: obj.km || obj.km_actuel || 0,
          prochain_service_km: obj.prochain_service_km,
          client_nom: obj.client_nom || obj.client,
          client_prenom: obj.client_prenom,
          client_email: obj.email,
          client_tel: obj.tel
        };
      });
      
      const res = await motosAPI.importCSV({ motos: motosData });
      setImportResult(res.data);
      if (res.data.imported > 0) {
        onUpdate();
      }
    } catch (err) {
      setImportResult({ error: err.message });
    }
  };

  // Enregistrer appel
  const handleSaveAppel = async () => {
    try {
      await tentativesAppelAPI.create({
        moto_id: showAppelModal.id,
        client_id: showAppelModal.client_id,
        ...appelForm
      });
      setShowAppelModal(null);
      setAppelForm({ resultat: '', notes: '', rappel_date: '', rappel_heure: '' });
      loadHistorique(showAppelModal.id);
    } catch (err) {
      console.error('Error saving appel:', err);
    }
  };

  const displayMotos = searchResults !== null ? searchResults : motos;

  // Calendar helpers
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const RESULTAT_APPEL = [
    { key: 'repondu', label: 'Répondu', color: 'var(--green)' },
    { key: 'pas_reponse', label: 'Pas de réponse', color: 'var(--yellow)' },
    { key: 'messagerie', label: 'Messagerie', color: 'var(--blue)' },
    { key: 'rappeler', label: 'À rappeler', color: 'var(--accent)' },
    { key: 'rdv_pris', label: 'RDV pris', color: 'var(--green)' },
  ];

  return (
    <div data-testid="parc-motos-view">
      {/* Header with tabs */}
      <div className="flex justify-between items-center mb-4">
        <div className="filter-tabs">
          <button className={`filter-tab ${view === 'liste' ? 'active' : ''}`} onClick={() => setView('liste')}>
            <Bike size={14} /> Parc complet
          </button>
          <button className={`filter-tab ${view === 'alertes' ? 'active' : ''}`} onClick={() => setView('alertes')}>
            <AlertTriangle size={14} /> Alertes révision
            {alertes.length > 0 && <span className="nav-badge ml-2">{alertes.length}</span>}
          </button>
          <button className={`filter-tab ${view === 'calendrier' ? 'active' : ''}`} onClick={() => setView('calendrier')}>
            <Calendar size={14} /> Calendrier rappels
          </button>
        </div>
        <div className="flex gap-2">
          <button 
            className="btn btn-ghost"
            onClick={async () => {
              try {
                const res = await exportAPI.motosAlertes();
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `motos_alertes_${new Date().toISOString().split('T')[0]}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
              } catch (err) {
                alert('Erreur export CSV');
              }
            }}
            data-testid="export-motos-csv-btn"
          >
            <Download size={14} /> Export alertes
          </button>
          <button className="btn btn-primary" onClick={() => setShowImportModal(true)}>
            <Upload size={14} /> Import CSV
          </button>
        </div>
      </div>

      {/* Search bar */}
      {view === 'liste' && (
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text3)]" />
            <input
              type="text"
              className="input w-full pl-10"
              placeholder="Rechercher par VIN, immatriculation ou nom client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              data-testid="search-input"
            />
          </div>
          <button className="btn btn-ghost" onClick={handleSearch}>Rechercher</button>
          {searchResults && (
            <button className="btn btn-ghost" onClick={() => { setSearchResults(null); setSearchQuery(''); }}>
              Effacer
            </button>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid mb-4">
        <div className="stat-card">
          <div className="stat-label">Parc total</div>
          <div className="stat-value">{motos.length}</div>
          <div className="stat-sub">motos enregistrées</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Alertes révision</div>
          <div className="stat-value" style={{ color: alertes.length > 0 ? 'var(--red)' : 'var(--green)' }}>
            {alertes.filter(a => a.urgence === 'depasse').length}
          </div>
          <div className="stat-sub">dépassées · {alertes.filter(a => a.urgence === 'urgent').length} urgentes</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Rappels planifiés</div>
          <div className="stat-value">{calendarRappels.length}</div>
          <div className="stat-sub">ce mois</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Clients actifs</div>
          <div className="stat-value">{clients.filter(c => c.statut === 'actif').length}</div>
          <div className="stat-sub">avec moto(s)</div>
        </div>
      </div>

      {/* Liste view */}
      {view === 'liste' && (
        <div className="card">
          <div className="card-body p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Moto</th>
                  <th>VIN</th>
                  <th>Immat.</th>
                  <th>Client</th>
                  <th>Km</th>
                  <th>Prochain service</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayMotos.map(moto => {
                  const client = getClient(moto.client_id);
                  const alerte = alertes.find(a => a.moto?.id === moto.id);
                  return (
                    <tr key={moto.id} data-testid={`moto-row-${moto.id}`}>
                      <td>
                        <div className="font-medium">{moto.marque} {moto.modele}</div>
                        <div className="text-xs text-[var(--text3)]">{moto.annee}</div>
                      </td>
                      <td className="font-mono text-xs">{moto.vin || '—'}</td>
                      <td>{moto.immatriculation || '—'}</td>
                      <td>
                        <div>{client?.prenom} {client?.nom}</div>
                        <div className="text-xs text-[var(--text3)]">{client?.tel}</div>
                      </td>
                      <td className="font-mono">{moto.km_actuel?.toLocaleString()} km</td>
                      <td>
                        {moto.prochain_service_km && (
                          <div className="flex items-center gap-2">
                            <span className="font-mono">{moto.prochain_service_km?.toLocaleString()} km</span>
                            {alerte && (
                              <span className={`badge ${alerte.urgence === 'depasse' ? 'badge-red' : alerte.urgence === 'urgent' ? 'badge-yellow' : 'badge-blue'}`}>
                                {alerte.urgence === 'depasse' ? 'Dépassé' : alerte.urgence === 'urgent' ? 'Urgent' : 'Proche'}
                              </span>
                            )}
                          </div>
                        )}
                        {moto.prochain_service_date && (
                          <div className="text-xs text-[var(--text3)]">{fmtDate(moto.prochain_service_date)}</div>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button 
                            className="btn btn-ghost btn-sm"
                            onClick={() => { setSelectedMoto(moto); loadHistorique(moto.id); }}
                            title="Voir fiche"
                          >
                            <FileText size={14} />
                          </button>
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => setShowAppelModal(moto)}
                            title="Enregistrer appel"
                          >
                            <PhoneCall size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alertes view */}
      {view === 'alertes' && (
        <div className="space-y-4">
          {alertes.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-12">
                <Check size={48} className="mx-auto text-[var(--green)] mb-4" />
                <p className="text-lg font-medium">Toutes les motos sont à jour !</p>
                <p className="text-[var(--text3)]">Aucune révision à planifier</p>
              </div>
            </div>
          ) : (
            alertes.map(({ moto, urgence, raison }) => {
              const client = getClient(moto.client_id);
              return (
                <div 
                  key={moto.id} 
                  className="card"
                  style={{ borderLeft: `4px solid ${urgence === 'depasse' ? 'var(--red)' : urgence === 'urgent' ? 'var(--yellow)' : 'var(--blue)'}` }}
                >
                  <div className="card-body">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className={`badge ${urgence === 'depasse' ? 'badge-red' : urgence === 'urgent' ? 'badge-yellow' : 'badge-blue'}`}>
                            {urgence === 'depasse' ? 'DÉPASSÉ' : urgence === 'urgent' ? 'URGENT' : 'PROCHE'}
                          </span>
                          <span className="font-condensed text-xl font-bold">{moto.marque} {moto.modele}</span>
                        </div>
                        <div className="text-sm text-[var(--text2)] mt-1">
                          {client?.prenom} {client?.nom} · {moto.immatriculation}
                        </div>
                        <div className="text-xs text-[var(--text3)] mt-2">
                          {raison.join(' · ')}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          className="btn btn-ghost"
                          onClick={() => { setSelectedMoto(moto); loadHistorique(moto.id); }}
                        >
                          <FileText size={14} /> Fiche
                        </button>
                        <button 
                          className="btn btn-primary"
                          onClick={() => setShowAppelModal(moto)}
                        >
                          <PhoneCall size={14} /> Appeler
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 grid-3">
                      <div>
                        <div className="text-xs text-[var(--text3)] uppercase">Km actuel</div>
                        <div className="font-mono font-bold">{moto.km_actuel?.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-[var(--text3)] uppercase">Service prévu</div>
                        <div className="font-mono font-bold">{moto.prochain_service_km?.toLocaleString() || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-[var(--text3)] uppercase">Contact</div>
                        <div className="font-mono">{client?.tel || client?.email || '—'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Calendrier view */}
      {view === 'calendrier' && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-4">
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => setCalendarMonth(prev => {
                  const newMonth = prev.month - 1;
                  return newMonth < 0 
                    ? { year: prev.year - 1, month: 11 }
                    : { ...prev, month: newMonth };
                })}
              >←</button>
              <span className="font-condensed font-bold text-lg uppercase">
                {MOIS_FR[calendarMonth.month]} {calendarMonth.year}
              </span>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => setCalendarMonth(prev => {
                  const newMonth = prev.month + 1;
                  return newMonth > 11 
                    ? { year: prev.year + 1, month: 0 }
                    : { ...prev, month: newMonth };
                })}
              >→</button>
            </div>
            <span className="badge badge-orange">{calendarRappels.length} rappels</span>
          </div>
          <div className="card-body">
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                <div key={day} className="text-center text-xs font-bold text-[var(--text3)] py-2">{day}</div>
              ))}
              {(() => {
                const daysInMonth = getDaysInMonth(calendarMonth.year, calendarMonth.month);
                let firstDay = getFirstDayOfMonth(calendarMonth.year, calendarMonth.month);
                firstDay = firstDay === 0 ? 6 : firstDay - 1; // Convert to Monday start
                const cells = [];
                
                // Empty cells before first day
                for (let i = 0; i < firstDay; i++) {
                  cells.push(<div key={`empty-${i}`} className="p-2" />);
                }
                
                // Day cells
                for (let day = 1; day <= daysInMonth; day++) {
                  const dateStr = `${calendarMonth.year}-${String(calendarMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayRappels = calendarRappels.filter(r => r.rappel_date === dateStr);
                  
                  cells.push(
                    <div 
                      key={day}
                      className={`p-2 min-h-[80px] rounded border ${dayRappels.length > 0 ? 'border-[var(--accent)]' : 'border-[var(--border)]'}`}
                      style={{ background: dayRappels.length > 0 ? 'var(--accent-dim)' : 'var(--bg3)' }}
                    >
                      <div className="text-xs font-bold mb-1">{day}</div>
                      {dayRappels.slice(0, 2).map((r, i) => {
                        const moto = motos.find(m => m.id === r.moto_id);
                        const client = getClient(r.client_id);
                        return (
                          <div key={i} className="text-xs truncate mb-1" title={`${client?.nom} - ${moto?.modele}`}>
                            <span className="text-[var(--accent)]">{r.rappel_heure}</span> {client?.nom?.substring(0, 8)}
                          </div>
                        );
                      })}
                      {dayRappels.length > 2 && (
                        <div className="text-xs text-[var(--text3)]">+{dayRappels.length - 2}</div>
                      )}
                    </div>
                  );
                }
                
                return cells;
              })()}
            </div>

            {/* Rappels list */}
            <div className="mt-6 pt-4 border-t border-[var(--border)]">
              <h3 className="font-condensed font-bold uppercase mb-3">Rappels du mois</h3>
              {calendarRappels.length === 0 ? (
                <p className="text-[var(--text3)]">Aucun rappel planifié ce mois</p>
              ) : (
                <div className="space-y-2">
                  {calendarRappels.map(rappel => {
                    const moto = motos.find(m => m.id === rappel.moto_id);
                    const client = getClient(rappel.client_id);
                    return (
                      <div key={rappel.id} className="flex items-center gap-4 p-3 rounded" style={{ background: 'var(--bg3)' }}>
                        <div className="text-center">
                          <div className="font-condensed text-lg font-bold" style={{ color: 'var(--accent)' }}>
                            {rappel.rappel_date?.split('-')[2]}
                          </div>
                          <div className="text-xs text-[var(--text3)]">{rappel.rappel_heure}</div>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{client?.prenom} {client?.nom}</div>
                          <div className="text-xs text-[var(--text3)]">{moto?.marque} {moto?.modele} · {rappel.notes}</div>
                        </div>
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={() => setShowAppelModal(moto)}
                        >
                          <PhoneCall size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Moto Detail Panel */}
      {selectedMoto && (
        <div className="panel-overlay" onClick={() => setSelectedMoto(null)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '600px' }}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="panel-title">{selectedMoto.marque} {selectedMoto.modele}</div>
                <div className="text-sm text-[var(--text3)]">{selectedMoto.annee} · {selectedMoto.immatriculation}</div>
              </div>
              <button className="panel-close" onClick={() => setSelectedMoto(null)}>
                <X size={20} />
              </button>
            </div>

            {/* Moto info */}
            <div className="panel-section">
              <div className="panel-section-title">Informations</div>
              <div className="grid-2 gap-4">
                <div>
                  <div className="text-xs text-[var(--text3)]">VIN</div>
                  <div className="font-mono text-sm">{selectedMoto.vin || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-[var(--text3)]">Km actuel</div>
                  <div className="font-mono">{selectedMoto.km_actuel?.toLocaleString()} km</div>
                </div>
                <div>
                  <div className="text-xs text-[var(--text3)]">Prochain service</div>
                  <div className="font-mono">{selectedMoto.prochain_service_km?.toLocaleString()} km</div>
                </div>
                <div>
                  <div className="text-xs text-[var(--text3)]">Date service</div>
                  <div>{fmtDate(selectedMoto.prochain_service_date)}</div>
                </div>
              </div>
            </div>

            {/* Client info */}
            <div className="panel-section">
              <div className="panel-section-title">Client</div>
              {(() => {
                const client = getClient(selectedMoto.client_id);
                return client ? (
                  <div className="space-y-2">
                    <div className="font-bold">{client.prenom} {client.nom}</div>
                    <div className="text-sm">{client.email}</div>
                    <div className="text-sm">{client.tel}</div>
                    <div className="text-sm text-[var(--text3)]">{client.ville}</div>
                  </div>
                ) : <p className="text-[var(--text3)]">Client non trouvé</p>;
              })()}
            </div>

            {/* Historique OT */}
            <div className="panel-section">
              <div className="panel-section-title">Historique interventions</div>
              {motoHistorique?.ordres_travail?.length > 0 ? (
                <div className="space-y-2">
                  {motoHistorique.ordres_travail.map(ot => (
                    <div key={ot.id} className="p-3 rounded" style={{ background: 'var(--bg3)' }}>
                      <div className="flex justify-between">
                        <span className="font-mono text-sm">{ot.reference}</span>
                        <span className={`badge ${OT_STATUTS[ot.statut]?.cls}`}>{OT_STATUTS[ot.statut]?.label}</span>
                      </div>
                      <div className="text-sm mt-1">{ot.description}</div>
                      <div className="text-xs text-[var(--text3)] mt-1">{fmtDate(ot.date_planifiee)} · {ot.vt_vendus} VT</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[var(--text3)]">Aucune intervention</p>
              )}
            </div>

            {/* Historique appels */}
            <div className="panel-section">
              <div className="panel-section-title">Historique appels</div>
              {motoHistorique?.tentatives_appel?.length > 0 ? (
                <div className="space-y-2">
                  {motoHistorique.tentatives_appel.map(t => {
                    const resultat = RESULTAT_APPEL.find(r => r.key === t.resultat);
                    return (
                      <div key={t.id} className="p-3 rounded" style={{ background: 'var(--bg3)' }}>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">{t.date} à {t.heure}</span>
                          <span className="badge" style={{ background: `${resultat?.color}22`, color: resultat?.color }}>
                            {resultat?.label}
                          </span>
                        </div>
                        {t.notes && <div className="text-xs text-[var(--text3)] mt-1">{t.notes}</div>}
                        {t.rappel_date && (
                          <div className="text-xs mt-1" style={{ color: 'var(--accent)' }}>
                            Rappel: {t.rappel_date} à {t.rappel_heure}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[var(--text3)]">Aucun appel enregistré</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 mt-6">
              <button className="btn btn-primary" onClick={() => { setShowAppelModal(selectedMoto); }}>
                <PhoneCall size={14} /> Enregistrer appel
              </button>
              <button className="btn btn-ghost">
                <Wrench size={14} /> Créer OT
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Import CSV */}
      {showImportModal && (
        <div className="panel-overlay" onClick={() => { setShowImportModal(false); setImportResult(null); setCsvData(''); }}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '600px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">Import CSV</div>
              <button className="panel-close" onClick={() => { setShowImportModal(false); setImportResult(null); setCsvData(''); }}>
                <X size={20} />
              </button>
            </div>

            <div className="panel-section">
              <div className="panel-section-title">Format attendu (séparateur: ;)</div>
              <div className="p-3 rounded font-mono text-xs" style={{ background: 'var(--bg)', border: '1px dashed var(--border2)' }}>
                vin;marque;modele;annee;immatriculation;km;prochain_service_km;client_nom;client_prenom;email;tel
              </div>
            </div>

            <div className="panel-section">
              <div className="panel-section-title">Données CSV</div>
              <textarea
                className="input w-full h-48 font-mono text-xs"
                placeholder="Collez vos données CSV ici..."
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
              />
            </div>

            {importResult && (
              <div className={`p-4 rounded mb-4 ${importResult.error ? 'bg-[var(--red-dim)]' : 'bg-[var(--green-dim)]'}`}>
                {importResult.error ? (
                  <p style={{ color: 'var(--red)' }}>Erreur: {importResult.error}</p>
                ) : (
                  <>
                    <p style={{ color: 'var(--green)' }}>✓ {importResult.imported} moto(s) importée(s) sur {importResult.total}</p>
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2 text-xs text-[var(--text3)]">
                        {importResult.errors.map((e, i) => <div key={i}>{e}</div>)}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <button className="btn btn-primary w-full" onClick={handleImportCSV} disabled={!csvData.trim()}>
              <Upload size={14} /> Importer
            </button>
          </div>
        </div>
      )}

      {/* Modal Enregistrer Appel */}
      {showAppelModal && (
        <div className="panel-overlay" onClick={() => setShowAppelModal(null)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '500px' }}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="panel-title">Enregistrer appel</div>
                <div className="text-sm text-[var(--text3)]">
                  {showAppelModal.marque} {showAppelModal.modele} · {getClient(showAppelModal.client_id)?.nom}
                </div>
              </div>
              <button className="panel-close" onClick={() => setShowAppelModal(null)}>
                <X size={20} />
              </button>
            </div>

            {/* Client info */}
            <div className="p-4 rounded mb-4" style={{ background: 'var(--bg3)' }}>
              {(() => {
                const client = getClient(showAppelModal.client_id);
                return (
                  <div className="flex items-center gap-4">
                    <Phone size={24} className="text-[var(--accent)]" />
                    <div>
                      <div className="font-bold">{client?.prenom} {client?.nom}</div>
                      <div className="text-lg font-mono">{client?.tel || 'Pas de téléphone'}</div>
                      <div className="text-xs text-[var(--text3)]">{client?.email}</div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Résultat */}
            <div className="panel-section">
              <div className="panel-section-title">Résultat de l'appel</div>
              <div className="grid grid-cols-3 gap-2">
                {RESULTAT_APPEL.map(r => (
                  <button
                    key={r.key}
                    className={`p-3 rounded text-sm font-medium transition-all ${appelForm.resultat === r.key ? 'ring-2' : ''}`}
                    style={{ 
                      background: appelForm.resultat === r.key ? `${r.color}22` : 'var(--bg3)',
                      color: appelForm.resultat === r.key ? r.color : 'var(--text2)',
                      borderColor: r.color
                    }}
                    onClick={() => setAppelForm({ ...appelForm, resultat: r.key })}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="panel-section">
              <div className="panel-section-title">Notes</div>
              <textarea
                className="input w-full h-20"
                placeholder="Notes sur l'appel..."
                value={appelForm.notes}
                onChange={(e) => setAppelForm({ ...appelForm, notes: e.target.value })}
              />
            </div>

            {/* Rappel */}
            {appelForm.resultat === 'rappeler' && (
              <div className="panel-section">
                <div className="panel-section-title">Planifier rappel</div>
                <div className="grid-2 gap-4">
                  <input
                    type="date"
                    className="input"
                    value={appelForm.rappel_date}
                    onChange={(e) => setAppelForm({ ...appelForm, rappel_date: e.target.value })}
                  />
                  <input
                    type="time"
                    className="input"
                    value={appelForm.rappel_heure}
                    onChange={(e) => setAppelForm({ ...appelForm, rappel_heure: e.target.value })}
                  />
                </div>
              </div>
            )}

            <button 
              className="btn btn-primary w-full mt-4" 
              onClick={handleSaveAppel}
              disabled={!appelForm.resultat}
            >
              <Check size={14} /> Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Bexio Sync Component
const BexioSync = ({ status }) => {
  const [syncing, setSyncing] = useState({});
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [offers, setOffers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBexioData();
  }, []);

  const loadBexioData = async () => {
    setLoading(true);
    try {
      const [summaryRes, invoicesRes, offersRes] = await Promise.all([
        bexioAPI.getSummary(),
        bexioAPI.getLocalInvoices(50),
        bexioAPI.getLocalOffers(50)
      ]);
      setSummary(summaryRes.data);
      setInvoices(invoicesRes.data);
      setOffers(offersRes.data);
    } catch (err) {
      console.error('Error loading Bexio data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (type) => {
    setSyncing({ ...syncing, [type]: true });
    try {
      if (type === 'contacts') {
        await bexioAPI.syncContacts();
      } else if (type === 'invoices') {
        await bexioAPI.syncInvoices();
      } else if (type === 'offers') {
        await bexioAPI.syncOffers();
      } else {
        // Sync all
        await Promise.all([
          bexioAPI.syncContacts(),
          bexioAPI.syncInvoices(),
          bexioAPI.syncOffers()
        ]);
      }
      await loadBexioData();
    } catch (err) {
      console.error('Sync error:', err);
      alert(err.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setSyncing({ ...syncing, [type]: false });
    }
  };

  const getAuthUrl = async () => {
    try {
      const res = await bexioAPI.getAuthUrl();
      window.open(res.data.auth_url, '_blank');
    } catch (err) {
      alert('Erreur génération URL auth');
    }
  };

  return (
    <div data-testid="bexio-view">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h2 className="font-condensed text-2xl font-bold uppercase">Synchronisation Bexio</h2>
          <span className={`badge ${status?.connected ? 'badge-green' : 'badge-red'}`}>
            {status?.connected ? 'Connecté' : 'Déconnecté'}
          </span>
        </div>
        <div className="flex gap-2">
          {!status?.connected && (
            <button className="btn btn-ghost" onClick={getAuthUrl}>
              Se connecter à Bexio
            </button>
          )}
          <button 
            className="btn btn-primary" 
            onClick={() => handleSync('all')}
            disabled={syncing.all || !status?.connected}
          >
            <RefreshCw size={14} className={syncing.all ? 'animate-spin' : ''} />
            Tout synchroniser
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['overview', 'invoices', 'offers'].map(tab => (
          <button
            key={tab}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === tab 
                ? 'bg-[var(--accent)] text-white' 
                : 'bg-[var(--bg3)] text-[var(--text2)] hover:text-white'
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' ? 'Vue d\'ensemble' : tab === 'invoices' ? `Factures (${invoices.length})` : `Offres (${offers.length})`}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid-3 gap-4">
          {/* Contacts Card */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Contacts</span>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => handleSync('contacts')}
                disabled={syncing.contacts || !status?.connected}
              >
                <RefreshCw size={12} className={syncing.contacts ? 'animate-spin' : ''} />
              </button>
            </div>
            <div className="card-body">
              <div className="text-3xl font-bold mb-2">{summary?.contacts?.count || 0}</div>
              <div className="text-xs text-[var(--text3)]">
                Dernière sync: {summary?.contacts?.last_sync ? fmtDate(summary.contacts.last_sync) : '—'}
              </div>
            </div>
          </div>

          {/* Invoices Card */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Factures</span>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => handleSync('invoices')}
                disabled={syncing.invoices || !status?.connected}
              >
                <RefreshCw size={12} className={syncing.invoices ? 'animate-spin' : ''} />
              </button>
            </div>
            <div className="card-body">
              <div className="text-3xl font-bold mb-2">{summary?.invoices?.count || 0}</div>
              <div className="text-xs text-[var(--text3)]">
                Dernière sync: {summary?.invoices?.last_sync ? fmtDate(summary.invoices.last_sync) : '—'}
              </div>
            </div>
          </div>

          {/* Offers Card */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Offres/Devis</span>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => handleSync('offers')}
                disabled={syncing.offers || !status?.connected}
              >
                <RefreshCw size={12} className={syncing.offers ? 'animate-spin' : ''} />
              </button>
            </div>
            <div className="card-body">
              <div className="text-3xl font-bold mb-2">{summary?.offers?.count || 0}</div>
              <div className="text-xs text-[var(--text3)]">
                Dernière sync: {summary?.offers?.last_sync ? fmtDate(summary.offers.last_sync) : '—'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Factures synchronisées</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  <th className="text-left p-3 text-xs text-[var(--text3)] uppercase">N° Document</th>
                  <th className="text-left p-3 text-xs text-[var(--text3)] uppercase">Titre</th>
                  <th className="text-right p-3 text-xs text-[var(--text3)] uppercase">Total</th>
                  <th className="text-left p-3 text-xs text-[var(--text3)] uppercase">Date</th>
                  <th className="text-left p-3 text-xs text-[var(--text3)] uppercase">Statut</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.bexio_id} className="border-b border-[var(--border)] hover:bg-[var(--bg3)]">
                    <td className="p-3 font-mono font-bold text-[var(--accent)]">{inv.document_nr}</td>
                    <td className="p-3">{inv.title || '—'}</td>
                    <td className="p-3 text-right font-mono">{fmtCHF(inv.total)}</td>
                    <td className="p-3 text-sm">{inv.is_valid_from ? fmtDate(inv.is_valid_from) : '—'}</td>
                    <td className="p-3">
                      <span className={`badge ${inv.kb_item_status_id === 7 ? 'badge-green' : inv.kb_item_status_id === 8 ? 'badge-red' : 'badge-yellow'}`}>
                        {inv.kb_item_status_id === 7 ? 'Payée' : inv.kb_item_status_id === 8 ? 'Annulée' : 'En attente'}
                      </span>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-[var(--text3)]">
                      Aucune facture synchronisée. Cliquez sur "Synchroniser" pour importer les factures Bexio.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Offers Tab */}
      {activeTab === 'offers' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Offres/Devis synchronisés</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  <th className="text-left p-3 text-xs text-[var(--text3)] uppercase">N° Document</th>
                  <th className="text-left p-3 text-xs text-[var(--text3)] uppercase">Titre</th>
                  <th className="text-right p-3 text-xs text-[var(--text3)] uppercase">Total</th>
                  <th className="text-left p-3 text-xs text-[var(--text3)] uppercase">Valide du</th>
                  <th className="text-left p-3 text-xs text-[var(--text3)] uppercase">Valide jusqu'au</th>
                  <th className="text-left p-3 text-xs text-[var(--text3)] uppercase">Statut</th>
                </tr>
              </thead>
              <tbody>
                {offers.map(offer => (
                  <tr key={offer.bexio_id} className="border-b border-[var(--border)] hover:bg-[var(--bg3)]">
                    <td className="p-3 font-mono font-bold text-[var(--accent)]">{offer.document_nr}</td>
                    <td className="p-3">{offer.title || '—'}</td>
                    <td className="p-3 text-right font-mono">{fmtCHF(offer.total)}</td>
                    <td className="p-3 text-sm">{offer.is_valid_from ? fmtDate(offer.is_valid_from) : '—'}</td>
                    <td className="p-3 text-sm">{offer.is_valid_until ? fmtDate(offer.is_valid_until) : '—'}</td>
                    <td className="p-3">
                      <span className={`badge ${offer.kb_item_status_id === 3 ? 'badge-green' : offer.kb_item_status_id === 4 ? 'badge-red' : 'badge-yellow'}`}>
                        {offer.kb_item_status_id === 3 ? 'Acceptée' : offer.kb_item_status_id === 4 ? 'Refusée' : 'En attente'}
                      </span>
                    </td>
                  </tr>
                ))}
                {offers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-[var(--text3)]">
                      Aucune offre synchronisée. Cliquez sur "Synchroniser" pour importer les offres Bexio.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Connection Info */}
      {!status?.connected && (
        <div className="card mt-4">
          <div className="card-body text-center py-8">
            <AlertTriangle size={32} className="mx-auto text-[var(--yellow)] mb-3" />
            <p className="text-sm text-[var(--text2)] mb-3">
              Connexion Bexio non établie
            </p>
            <p className="text-xs text-[var(--text3)]">
              Cliquez sur "Se connecter à Bexio" pour autoriser l'accès à votre compte Bexio.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Main App Layout
const MainLayout = () => {
  const [view, setView] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [techniciens, setTechniciens] = useState([]);
  const [clients, setClients] = useState([]);
  const [motos, setMotos] = useState([]);
  const [ots, setOTs] = useState([]);
  const [slots, setSlots] = useState([]);
  const [leads, setLeads] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [bexioStatus, setBexioStatus] = useState(null);
  const [testRides, setTestRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const loadData = async () => {
    try {
      const [statsRes, techRes, clientsRes, motosRes, otsRes, slotsRes, leadsRes, bexioRes, prestationsRes, absencesRes, testRidesRes] = await Promise.all([
        dashboardAPI.getStats(),
        techniciensAPI.getAll(),
        clientsAPI.getAll(),
        motosAPI.getAll(),
        ordresTravailAPI.getAll(),
        planningAPI.getSlots(),
        leadsAPI.getAll(),
        bexioAPI.getStatus(),
        prestationsAPI.getAll(),
        absencesAPI.getAll(),
        testRidesAPI.getAll().catch(() => ({ data: [] })), // Graceful fallback
      ]);
      
      setStats(statsRes.data);
      setTechniciens(techRes.data);
      setClients(clientsRes.data);
      setMotos(motosRes.data);
      setOTs(otsRes.data);
      setSlots(slotsRes.data);
      setLeads(leadsRes.data);
      setBexioStatus(bexioRes.data);
      setPrestations(prestationsRes.data);
      setAbsences(absencesRes.data);
      setTestRides(testRidesRes.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const viewTitles = {
    dashboard: 'Dashboard',
    'dashboard-atelier': 'Dashboard Atelier',
    ot: 'Ordres de Travail',
    planning: 'Planning Atelier',
    'saisie-vt': 'Saisie VT Réalisés',
    'parc-motos': 'Parc Motos / VIN',
    pipeline: 'Pipeline Vente',
    'test-rides': 'Test Rides',
    equipe: 'Équipe',
    automatismes: 'Automatismes',
    bexio: 'Sync Bexio',
    licenses: 'Gestion des Licences',
    'audit-log': 'Historique des modifications',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--bg)]">
        <RefreshCw className="animate-spin text-[var(--accent)]" size={32} />
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar 
        currentView={view} 
        setView={setView} 
        bexioStatus={bexioStatus} 
        isMobileOpen={mobileMenuOpen}
        setMobileOpen={setMobileMenuOpen}
      />
      
      <main className="main-content">
        <header className="topbar">
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </button>
          <h1 className="topbar-title">{viewTitles[view]}</h1>
          <div className="flex items-center gap-4">
            <NotificationCenter />
          </div>
        </header>
        
        <div className="content">
          {view === 'dashboard' && (
            <AdaptiveDashboard 
              setView={setView} 
              stats={stats} 
              ots={ots} 
              techniciens={techniciens}
              clients={clients}
              motos={motos}
              leads={leads}
              absences={absences}
              bexioStatus={bexioStatus}
              testRides={testRides}
            />
          )}
          {view === 'dashboard-atelier' && (
            <DashboardAtelier 
              ots={ots} 
              techniciens={techniciens}
              clients={clients}
              motos={motos}
              slots={slots}
            />
          )}
          {view === 'ot' && (
            <OrdresTravail 
              ots={ots} 
              techniciens={techniciens}
              clients={clients}
              motos={motos}
              prestations={prestations}
              onUpdate={loadData}
            />
          )}
          {view === 'planning' && (
            <Planning 
              slots={slots}
              techniciens={techniciens}
              ots={ots}
              clients={clients}
              motos={motos}
              absences={absences}
              onUpdate={loadData}
            />
          )}
          {view === 'saisie-vt' && (
            <SaisieVT />
          )}
          {view === 'parc-motos' && (
            <ParcMotos 
              motos={motos}
              clients={clients}
              ots={ots}
              onUpdate={loadData}
            />
          )}
          {view === 'pipeline' && (
            <Pipeline leads={leads} clients={clients} />
          )}
          {view === 'test-rides' && (
            <TestRides />
          )}
          {view === 'equipe' && (
            <Equipe techniciens={techniciens} onUpdate={loadData} />
          )}
          {view === 'automatismes' && (
            <Automatismes clients={clients} motos={motos} leads={leads} ots={ots} />
          )}
          {view === 'bexio' && (
            <BexioSync status={bexioStatus} />
          )}
          {view === 'licenses' && (
            <LicenseAdmin />
          )}
          {view === 'audit-log' && (
            <AuditLogView />
          )}
        </div>
      </main>
    </div>
  );
};

// App with Router
function App() {
  return (
    <AuthProvider>
      <LicenseProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/*" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </LicenseProvider>
    </AuthProvider>
  );
}

export default App;
